"use client";

import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import CdnImage from "@/components/CdnImage";
import Link from "next/link";
import { Loader2, X, Handshake, Target, User, Flag, Trophy, ArrowUpDown, Award, Swords, Crown, Skull, Flame, Shield, Star, Zap, Hash, Sliders } from "lucide-react";
import { FiBell } from "react-icons/fi";
import { ChevronLeftIcon } from "@/app/components/icons/ChevronLeftIcon";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { reportDextoolsConversion, detectAndStoreDextoolsUtm } from "@/lib/dextools-tracking";
import PriceAlertModal from "@/app/components/PriceAlertModal";
import OpenOrders from "@/app/market/[slug]/components/OpenOrders";
import MyPositions from "@/app/market/[slug]/components/MyPositions";
import SourceAwareOrderBook from "@/app/market/[slug]/components/SourceAwareOrderBook";
import SourceAwareTrades from "@/app/market/[slug]/components/SourceAwareTrades";
import MarketTalks from "@/app/market/[slug]/components/MarketTalks";
import RealTradingPanel from "@/app/market/[slug]/components/TradingPanel";
import EventMultiOutcomeChart from "@/app/components/EventMultiOutcomeChart";
import TennisHero from "./components/TennisHero";
import ResolutionDetails from "@/app/event/[slug]/components/ResolutionDetails";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import { useDextoolsSignupStore } from "@/stores/useDextoolsSignupStore";
import DextoolsLandingBar from "@/app/components/DextoolsLandingBar";
import { useT } from "@/hooks/useT";
import { useDisplayByType } from "./hooks/useDisplayByType";
import { useAllLivePrices } from "./hooks/useAllLivePrices";
import MobileBottomSheet from "@/app/event/[slug]/components/MobileBottomSheet";
import type { Market as EventMarket } from "@/app/event/[slug]/components/types";
import { BookOpenTextIcon } from "@/app/components/icons/BookOpenTextIcon";
import { BookmarkIcon } from "@/app/components/icons/BookmarkIcon";
import WhaleIcon from "@/app/components/icons/WhaleIcon";
import Tooltip from "@/app/components/Tooltip";
import { MarketPriceProvider } from "@/app/market/[slug]/context/MarketPriceContext";
import { useMarketPageQuery } from "@/hooks/useMarketPageQuery";
import { useLivePrices } from "@/hooks/useClobWebSocket";
import { useSmartActivityMap } from "@/hooks/useSmartActivityMap";
import { useMatchingMarkets } from "@/hooks/useMatchingMarkets";
import { useMarketWatchlistOptional } from "@/contexts/MarketWatchlistContext";
import { extractKalshiEventTicker } from "@/lib/source-url";
import { playerSlug, hasPlayerProfilePage } from "@/lib/sports-player-slug";
import {
  parseScore,
  formatPrice,
  formatVolume,
  abbreviateTennisName,
  humanizePeriod,
  LEAGUE_NAMES,
} from "./helpers/formatting";
import { useEventMarkets } from "./hooks/useEventMarkets";
import { useSportsEventLive } from "./hooks/useSportsEventLive";
import {
  shortenTitleWithAliases,
  stripOutcomeRecord,
  abbreviateOutcomesForSports,
  prettifyGameLineLabel,
  MAPS_LEAGUES,
  isMapsLeague,
  cleanMarketLabel,
} from "./helpers/market-labels";
import { getStreamData } from "@/app/components/sports2/league-config";
import { LivestreamPiP } from "./components/LivestreamPiP";
import { DragScrollRow } from "./components/DragScrollRow";
import { PriceChart } from "./components/PriceChart";
import { MoreGames } from "./components/MoreGames";
import { OrderBookCard } from "./components/OrderBookCard";
import type {
  Team,
  Teams,
  Game,
  MarketData,
  EventData,
  OrderBookData,
  MarketTypeGroup,
} from "./types";

// Predexon outage: ocultar solo los badges dependientes de Predexon del hero
// de sports (whale/smart cohort + "Also on Kalshi"). El resto de la página intacto.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

// =============================================================================
// Main Component
// =============================================================================
interface SportsEventClientProps {
  /** Full PM event payload fetched server-side. Carries description,
   *  endDate, tags, full markets list — fields the trimmed sports-live
   *  endpoint omits but ResolutionDetails / FAQ generation need. */
  fullEventData?: any | null;
  /** Pre-generated FAQ items from `generateEventFAQs`. */
  faqs?: Array<{ question: string; answer: string }>;
  /** SSR-fetched per-event sports-live payload (game/teams/event with
   *  live=true + score + period). Seeds `useSportsEventLive` so the
   *  first paint already has the live state — no spinner, no
   *  "first visit shows live=false" perception. The hook still owns
   *  the SSE / 15s polling / WS price subscriptions for refresh, so
   *  freshness is preserved. */
  initialSportsLive?: any | null;
  /** SSR-fetched markets-by-type so the probability bar + market tabs paint on
   *  first render instead of after the client fetch. */
  initialMarketsByType?: Record<string, any> | null;
}

// "World Cup Winner" event artwork (cached on our CDN) — reused as the hero
// thumbnail for individual World Cup matches, whose own gamma image is just a
// generic soccer ball. 2026-06-10.
const WORLD_CUP_HERO_IMAGE = 'https://images.frenflow.com/kalshi-cache/8ff485d7eac2f92dc6c9b56cac14d302.jpg';

export default function SportsEventClient({ fullEventData = null, faqs = [], initialSportsLive = null, initialMarketsByType = null }: SportsEventClientProps = {}) {
  const params = useParams();
  const pathname = usePathname();
  const isEsportsRoute = pathname?.startsWith('/esports');
  const slug = params?.slug as string;
  const { isLoggedIn, login } = useAuth();

  // Core data feed: initial fetch + 15s gamma polling fallback (live
  // games only) + SSE for scores & prices. Owns its own retry & flash
  // animation lifecycle. SSR data passed via `initialSportsLive`
  // pre-seeds game/teams/event so the first paint skips the spinner.
  const {
    loading,
    error,
    game,
    setGame,
    teams,
    event,
    sseScore,
    ssePeriod,
    scoreFlash,
    sseLivePrices,
  } = useSportsEventLive(slug, initialSportsLive);
  const [faqHovered, setFaqHovered] = useState(false);

  // Markets
  // Markets-by-type loader with active-tab + selected-market +
  // selected-outcome state for the trading panel.
  const {
    marketsByType,
    activeMarketType,
    setActiveMarketType,
    selectedMarketIdx,
    setSelectedMarketIdx,
    selectedOutcomeIdx,
    setSelectedOutcomeIdx,
  } = useEventMarkets(slug, useSearchParams().get("outcome"), initialMarketsByType);

  // Trading
  const [amount, setAmount] = useState("");
  const [side, setSide] = useState<"BUY" | "SELL">("BUY");
  const [orderType, setOrderType] = useState<"market" | "limit">("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [expiresIn, setExpiresIn] = useState<number | null>(null);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [tradeSuccess, setTradeSuccess] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);

  // UI feedback
  const [tradingFlash, setTradingFlash] = useState(false);

  // Bottom tabs
  const [bottomTab, setBottomTab] = useState<"positions" | "orders">("positions");
  // Mobile flow: tapping a moneyline outcome opens MobileBottomSheet (same
  // popover pattern /event uses on phones). The sheet hosts the price
  // alert bell + a `Buy` button that hands off to the global TradingModal.
  const [mobileTradeOpen, setMobileTradeOpen] = useState(false);
  const { openPolymarket } = useTradingModalStore();
  const t = useT();

  // DexTools widget landing → feed the global signup/analytics context with this
  // match's data so the "via DexTools · Official Partner" bar shows and the
  // referral landing is attributed. Mirrors EventClient; event-level (no single
  // outcome) since match deep-links don't target one side. Self-clears on leave.
  const setDextoolsSignupCtx = useDextoolsSignupStore((s) => s.setCtx);
  useEffect(() => {
    try {
      const utm = detectAndStoreDextoolsUtm();
      const title = event?.title
        || (teams ? `${teams.home?.name ?? ""} vs ${teams.away?.name ?? ""}`.trim() : "");
      if (!utm || utm.utmCampaign !== "predictions" || !title) {
        setDextoolsSignupCtx(null);
        return;
      }
      const crests = [teams?.home?.logo, teams?.away?.logo].filter((s): s is string => !!s);
      setDextoolsSignupCtx({
        marketTitle: title,
        marketImage: event?.image || null,
        outcomeName: null,
        outcomeImage: null,
        side: null,
        priceCents: null,
        outcomeImages: crests.length ? crests : undefined,
        outcomeCount: event?.markets?.length || (crests.length || undefined),
        volumeUsd: (() => { const n = Number(event?.volume); return Number.isFinite(n) && n > 0 ? n : null; })(),
      });
    } catch {
      setDextoolsSignupCtx(null);
    }
  }, [event?.title, event?.image, event?.volume, event?.markets, teams, setDextoolsSignupCtx]);
  // Clear when leaving so the bar/analytics don't linger on the next page.
  useEffect(() => () => setDextoolsSignupCtx(null), [setDextoolsSignupCtx]);

  const [activeLivestream, setActiveLivestream] = useState<{ type: string; channel: string; embedUrl: string } | null>(null);


  // CLOB WebSocket live prices for moneyline market
  const moneylineTokenIds = useMemo(() => {
    // Find moneyline market: could be under 'moneyline', 'game_lines', or 'series'
    const mlMarkets = marketsByType?.moneyline?.markets
      || marketsByType?.game_lines?.markets?.filter((m: any) => m.sportsMarketType === 'moneyline')
      || marketsByType?.series?.markets?.filter((m: any) => m.sportsMarketType === 'moneyline');
    const ml = mlMarkets?.[0];
    if (ml?.clobTokenIds && ml.clobTokenIds.length >= 2) return ml.clobTokenIds;
    // Fallback to first event market
    const m0 = event?.markets?.[0];
    if (m0?.clobTokenIds && m0.clobTokenIds.length >= 2) return m0.clobTokenIds;
    return undefined;
  }, [marketsByType, event?.markets]);

  const clobLivePrices = useLivePrices(moneylineTokenIds);

  // Merge: SSE prices (from backend) > CLOB WS prices (direct) > static
  const mergedLivePrices = useMemo(() => {
    if (sseLivePrices?.[0] != null && sseLivePrices?.[1] != null) return sseLivePrices;
    if (clobLivePrices[0] != null || clobLivePrices[1] != null) return clobLivePrices;
    return null;
  }, [sseLivePrices, clobLivePrices]);

  // Live prices for ALL markets via CLOB WebSocket — see hooks/useAllLivePrices.ts
  const allLivePrices = useAllLivePrices(marketsByType);

  // Derived market state
  // We expose the same 4-tab grouping the UI renders (moneyline / total
  // sets / total games / 1st set merged) via `displayByType`, then read
  // currentMarket from THAT structure. Without this, clicking the merged
  // "1st Set" tab set activeMarketType="first_set" — a key that doesn't
  // exist in raw `marketsByType` — and currentMarket fell to undefined,
  // which made the right column trading panel + orderbook disappear.
  // Derived market state—see hooks/useDisplayByType.ts.
  const displayByType = useDisplayByType(marketsByType, t);

  // Resolve active type — fallback to first available if current doesn't exist
  const resolvedMarketType = displayByType?.[activeMarketType] ? activeMarketType : Object.keys(displayByType || {})[0] || activeMarketType;
  const currentTypeData = displayByType?.[resolvedMarketType];
  const currentMarket = currentTypeData?.markets?.[selectedMarketIdx];

  // Detail fetch for the selected market — same hook the regular /event
  // page uses. Brings in description, resolutionSource, createdAt,
  // resolvedBy, settlementTimerSeconds, etc., none of which the trimmed
  // sports-live payload carries. ResolutionDetails reads
  // `selectedMarketDetails.market.<field>`, so we hand the result through
  // unchanged.
  const { data: selectedMarketDetails } = useMarketPageQuery(
    currentMarket?.slug || '',
    false,
  );
  const currentTokenId = currentMarket?.clobTokenIds?.[selectedOutcomeIdx]
    ?? event?.markets?.[0]?.clobTokenIds?.[selectedOutcomeIdx]
    ?? null;
  // Price calculation — uses best ask for BUY, best bid for SELL
  const price = useMemo(() => {
    if (currentMarket?.outcomePrices?.[selectedOutcomeIdx] != null) return Number(currentMarket.outcomePrices[selectedOutcomeIdx]) || 0.5;
    return 0.5;
  }, [currentMarket, selectedOutcomeIdx]);

  // Live outcomePrices for the selected market — overlays the static
  // gamma snapshot with `allLivePrices` (CLOB WS bestAsk we already
  // subscribe to for the moneyline buttons). Without this overlay the
  // sidebar TradingPanel hangs on the initial gamma price because its
  // own backend Socket.IO relay is intermittent. Reusing `allLivePrices`
  // means the buttons and the place-trade panel share a single source.
  const currentMarketLivePrices = useMemo<number[]>(() => {
    if (!currentMarket?.clobTokenIds) return [];
    const staticPrices = (currentMarket.outcomePrices || []).map((p: any) => Number(p) || 0);
    return currentMarket.clobTokenIds.map((tid: string, i: number) => {
      const live = tid ? allLivePrices[tid] : undefined;
      // 0 is a valid sentinel from the WS callback meaning "orderbook
      // has no real ask/bid" (degenerate side-market). Forward it
      // instead of falling back to gamma's static 0.5.
      return typeof live === 'number' && live >= 0 && live < 1 ? live : (staticPrices[i] ?? 0.5);
    });
  }, [currentMarket?.clobTokenIds, currentMarket?.outcomePrices, allLivePrices]);

  const amountNum = parseFloat(amount) || 0;
  const shares = amountNum > 0 ? amountNum / price : 0;
  const potentialWin = shares > 0 ? shares - amountNum : 0;

  // Score display — prefer SSE (freshest), fallback to game.score (full format with sets)
  // For esports (Bo format): show series score as main, map score as secondary
  const displayScore = useMemo(() => {
    const rawScore = sseScore || game?.score || "";
    if (rawScore) {
      const parsed = parseScore(rawScore);
      // Esports: swap series as main score (1-1 more relevant than 000-000 map score)
      if (parsed.format?.match(/^Bo\d+$/i) && parsed.seriesHome != null && parsed.seriesAway != null) {
        return {
          ...parsed,
          home: parsed.seriesHome,
          away: parsed.seriesAway,
          mapHome: parsed.home,
          mapAway: parsed.away,
          seriesHome: undefined,
          seriesAway: undefined,
        };
      }
      return parsed;
    }
    return { home: String(teams?.home?.score ?? ""), away: String(teams?.away?.score ?? "") };
  }, [sseScore, game?.score, teams]);

  // Winner detection for ended games
  const heroWinner = useMemo(() => {
    if (!game?.ended) return null;
    const h = parseInt(displayScore.home) || 0;
    const a = parseInt(displayScore.away) || 0;
    if (h === a) return null;
    return h > a ? 'home' : 'away';
  }, [game?.ended, displayScore.home, displayScore.away]);

  // Moneyline for price bar — prefer live prices (SSE > CLOB WS > static)
  const moneylineMarket = marketsByType?.moneyline?.markets?.[0];
  const homeProb = (mergedLivePrices?.[0] ?? moneylineMarket?.outcomePrices?.[0]) || 0;
  const awayProb = (mergedLivePrices?.[1] ?? moneylineMarket?.outcomePrices?.[1]) || 0;
  const probTotal = homeProb + awayProb;
  const homePct = probTotal > 0 ? Math.round((homeProb / probTotal) * 100) : 0;
  const awayPct = probTotal > 0 ? 100 - homePct : 0;

  const leagueName = event?.series?.[0]?.title || LEAGUE_NAMES[game?.league || ''] || game?.league?.toUpperCase() || "Sports";
  // World Cup matches: Polymarket's per-match gamma image is just a generic
  // soccer ball, so reuse the "World Cup Winner" event artwork (the proper
  // tournament graphic, already cached on our CDN) for the hero thumbnail.
  const isWorldCup = game?.league === 'fifwc' || /world cup/i.test(leagueName || '');
  const heroImage = isWorldCup ? WORLD_CUP_HERO_IMAGE : (event?.image || null);

  // Parse tournament from event title (e.g. "Dubrovnik: Dominika Salkova vs Kaitlin Quevedo")
  const tournament = useMemo(() => {
    const title = event?.title || '';
    const colonIdx = title.indexOf(':');
    if (colonIdx > 0 && colonIdx < 40) return title.substring(0, colonIdx).trim();
    return null;
  }, [event?.title]);

  // Kickoff shown in the USER'S LOCAL timezone (matches the markets grid /
  // WorldCupView cards). The UTC reference is kept in the hover title so users
  // can still confirm the canonical time. Returns { label, title }.
  const startTimeDisplay = useMemo(() => {
    const ts = game?.startTime || game?.timestamp;
    if (!ts) return null;
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return null;
      const isSpanish = t("time.today", { time: "" }).startsWith("Hoy");
      const loc = isSpanish ? 'es-ES' : 'en-US';
      // Local date + time (hour12 forces AM/PM regardless of locale 24h pref).
      const datePart = d.toLocaleDateString(loc, { month: 'short', day: 'numeric' }).replace(/\.$/, '');
      const timePart = d.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit', hour12: true });
      const label = `${datePart} · ${timePart}`;
      // UTC reference for the tooltip.
      const uh = d.getUTCHours().toString().padStart(2, '0');
      const um = d.getUTCMinutes().toString().padStart(2, '0');
      const uMonth = d.toLocaleString(loc, { month: 'short', timeZone: 'UTC' }).replace(/\.$/, '');
      const uDay = d.getUTCDate();
      const utc = isSpanish ? `${uDay} ${uMonth} · ${uh}:${um} UTC` : `${uMonth} ${uDay} · ${uh}:${um} UTC`;
      const title = isSpanish ? `Tu hora local — ${utc}` : `Your local time — ${utc}`;
      return { label, title };
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.startTime, game?.timestamp]);
  const marketTypes = marketsByType ? Object.keys(marketsByType) : [];
  const marketsCount = marketsByType
    ? Object.values(marketsByType).reduce((sum, g) => sum + g.markets.length, 0)
    : event?.marketsCount || event?.markets?.length || 0;

  // Pick the highest-volume sub-market as the "leader" for hero context badges
  // (smart-money cohort + cross-venue Kalshi match). Mirrors EventHero so a
  // sports match feels visually peer to the regular /event page.
  const leaderConditionId = useMemo(() => {
    if (!marketsByType) return null;
    let best: { cid: string; vol: number } | null = null;
    for (const group of Object.values(marketsByType)) {
      for (const m of group.markets || []) {
        const cid = (m as any).conditionId;
        if (!cid) continue;
        const v = Number((m as any).volume || 0);
        if (!best || v > best.vol) best = { cid, vol: v };
      }
    }
    return best?.cid || null;
  }, [marketsByType]);

  const { data: cohortMap } = useSmartActivityMap(500, true);
  const cohortRow = useMemo(() => {
    if (!cohortMap || !marketsByType) return null;
    let best: { wallets: number; volume: number } | null = null;
    for (const group of Object.values(marketsByType)) {
      for (const m of group.markets || []) {
        const cid = ((m as any).conditionId || '').toLowerCase();
        if (!cid) continue;
        const row = cohortMap.get(cid);
        if (!row) continue;
        if (!best || row.smartWalletCount > best.wallets) {
          best = { wallets: row.smartWalletCount, volume: row.smartVolume };
        }
      }
    }
    return best;
  }, [cohortMap, marketsByType]);
  const isWhaleMarket = !HIDE_PREDEXON && !!cohortRow && cohortRow.volume > 500_000 && cohortRow.wallets >= 10;
  const isSmartHeavy = !HIDE_PREDEXON && !!cohortRow && cohortRow.wallets >= 30 && !isWhaleMarket;

  const { data: matchData } = useMatchingMarkets(leaderConditionId, true);
  const kalshiMatches = matchData?.matches?.KALSHI;
  const hasExactMatch = !!(kalshiMatches?.exact && kalshiMatches.exact.length > 0);
  const hasRelatedMatch = !!(kalshiMatches?.related && kalshiMatches.related.length > 0);
  const hasAnyMatch = !HIDE_PREDEXON && (hasExactMatch || hasRelatedMatch);

  // Watchlist — same key shape as EventHero. Pick the leader market's
  // conditionId so entries keep a stable ref even as markets churn.
  const watchlistContext = useMarketWatchlistOptional();
  const eventWatchlistKey = slug || event?.slug;
  const isBookmarked = eventWatchlistKey ? (watchlistContext?.isInWatchlist(eventWatchlistKey) ?? false) : false;

  // Event-scope inputs for MyPositions: include EVERY sub-market in the event
  // so a position bought on one sub-market (e.g. Draw) still surfaces while
  // the user is browsing another (e.g. Moneyline). Mirrors event/[slug]
  // behavior — the single-market filter that lived here was hiding
  // user positions whose conditionId didn't match the currently selected
  // market.
  const allEventConditionIds = useMemo(
    () =>
      marketsByType
        ? Object.values(marketsByType).flatMap((g) =>
            (g.markets || []).map((m) => m.conditionId).filter((c): c is string => !!c),
          )
        : [],
    [marketsByType],
  );
  const allEventMarketSlugs = useMemo(
    () =>
      marketsByType
        ? Object.values(marketsByType).flatMap((g) =>
            (g.markets || []).map((m) => m.slug).filter((s): s is string => !!s),
          )
        : [],
    [marketsByType],
  );
  const marketMetaByConditionId = useMemo(() => {
    const out: Record<string, { image?: string | null; label: string }> = {};
    if (!marketsByType) return out;
    for (const group of Object.values(marketsByType)) {
      for (const m of group.markets || []) {
        if (!m.conditionId) continue;
        out[m.conditionId.toLowerCase()] = {
          image: (m as any).image || event?.image || null,
          label: m.groupItemTitle || m.question || m.slug || '',
        };
      }
    }
    return out;
  }, [marketsByType, event?.image]);

  // Memoize chart markets to prevent infinite re-fetching
  const chartMarkets = useMemo(() => {
    // Find moneyline markets: could be under 'moneyline', 'game_lines', 'series', or first tab
    const ml = marketsByType?.moneyline?.markets
      || marketsByType?.game_lines?.markets?.filter((m: any) => m.sportsMarketType === 'moneyline')
      || marketsByType?.series?.markets?.filter((m: any) => m.sportsMarketType === 'moneyline')
      || null;
    if (!ml || ml.length === 0) return [];
    // Binary moneyline (1 market, 2 outcomes): split into 2 virtual markets for 2 chart lines
    if (ml.length === 1 && ml[0].outcomes?.length === 2 && ml[0].clobTokenIds?.length >= 2) {
      const m = ml[0];
      return m.outcomes.map((outcome: string, i: number) => ({
        id: `${m.id}_${i}`,
        question: outcome,
        groupItemTitle: outcome,
        slug: m.slug,
        outcomes: [outcome],
        outcomePrices: [m.outcomePrices?.[i] ?? 0],
        clobTokenIds: [m.clobTokenIds[i]],
        volume: m.volume,
      }));
    }
    return ml.map((m: any) => ({
      id: m.id,
      question: m.question,
      groupItemTitle: m.groupItemTitle,
      slug: m.slug,
      outcomes: m.outcomes,
      outcomePrices: m.outcomePrices,
      clobTokenIds: m.clobTokenIds,
      volume: m.volume,
    }));
  }, [marketsByType?.moneyline?.markets, marketsByType?.game_lines?.markets, marketsByType?.series?.markets]);

  // Trade execution — supports market and limit orders
  const executeTrade = async () => {
    if (!isLoggedIn) { login(); return; }
    if (!currentTokenId || amountNum <= 0) return;

    setTradeLoading(true);
    setTradeError(null);
    setTradeSuccess(false);

    try {
      if (orderType === "limit") {
        const lp = parseFloat(limitPrice);
        if (!lp || lp < 1 || lp > 99) throw new Error("Limit price must be 1–99¢");
        const res = await authFetch("/api/trade/limit-order", {
          method: "POST",
          body: JSON.stringify({
            tokenId: currentTokenId,
            side,
            price: lp / 100,
            amount: amountNum,
            ...(expiresIn && { expiresIn }),
          }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || "Limit order failed");
      } else {
        const res = await authFetch("/api/trade/execute", {
          method: "POST",
          body: JSON.stringify({ tokenId: currentTokenId, side, price, amount: amountNum }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || "Trade failed");
      }
      setTradeSuccess(true);
      reportDextoolsConversion(slug, `trade_${side.toLowerCase()}`, amountNum);
      setAmount("");
      setTimeout(() => setTradeSuccess(false), 3000);
    } catch (err: any) {
      setTradeError(err.message || "Trade failed");
      setTimeout(() => setTradeError(null), 5000);
    } finally {
      setTradeLoading(false);
    }
  };

  // Split/Merge execution
  const executeSplitMerge = async (action: "split" | "merge", splitMergeAmount: number) => {
    if (!isLoggedIn || !currentMarket?.conditionId) return;
    setTradeLoading(true);
    setTradeError(null);
    try {
      const res = await authFetch(`/api/trade/${action}`, {
        method: "POST",
        body: JSON.stringify({ conditionId: currentMarket.conditionId, amount: splitMergeAmount }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || `${action} failed`);
      setTradeSuccess(true);
      setTimeout(() => setTradeSuccess(false), 3000);
    } catch (err: any) {
      setTradeError(err.message || `${action} failed`);
      setTimeout(() => setTradeError(null), 5000);
    } finally {
      setTradeLoading(false);
    }
  };

  // =========================================================================
  // Loading / Error
  // =========================================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not a real match (futures/championship) — show link to normal event page
  const isNotMatch = !loading && teams && (!teams.away?.name || teams.home?.name === teams.away?.name);

  if (isNotMatch) {
    if (typeof window !== 'undefined') {
      // Inside a Discord Activity iframe, a hard-redirect to
      // /polymarket/event/<slug> gets eaten by the `/` URL mapping and
      // lands on /embed/discord-activity/polymarket/event/<slug>, which
      // doesn't exist. Render a polite fallback instead of bouncing the
      // user out of the embed tree.
      const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
      if (!inIframe) {
        window.location.replace(`/polymarket/event/${slug}`);
      }
    }
    return (
      <div className="flex items-center justify-center min-h-[60vh] flex-col gap-3 text-center px-4">
        <div className="text-sm text-gray-500">{t("sportsEvent.notHeadToHead")}</div>
        <a
          href={`https://www.frenflow.com/polymarket/event/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#CCFF00] hover:underline"
        >
          Open on frenflow.com →
        </a>
      </div>
    );
  }

  if (error || !game || !teams) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link href={isEsportsRoute ? "/polymarket/markets/esports" : "/polymarket/markets/sports"} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors mb-6">
          <ChevronLeftIcon size={18} className="text-gray-400" />
          <span className="font-inter-tight font-medium">Back to {isEsportsRoute ? 'Esports' : 'Sports'}</span>
        </Link>
        <div className="text-center py-12">
          <p className="font-inter-tight text-red-500 text-lg">{error || "Game not found"}</p>
        </div>
      </div>
    );
  }

  const period = ssePeriod || game.period;

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <div className="w-full pt-2 md:pt-4 pb-24 lg:pb-12">

      {/* ================================================================= */}
      {/* GRID — 2 cols on lg+, 1 col mobile                               */}
      {/* ================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* ── LEFT COLUMN ────────────────────────────────── */}
        <div className="lg:col-span-9 min-w-0 space-y-4">

      {/* DexTools landing co-branding — self-gated (only renders on a real
          DexTools widget arrival while logged out). */}
      <DextoolsLandingBar fullWidth />

      {/* HERO — Matchup card */}
      <div className="bg-white dark:bg-[#141418] rounded-[5px] border border-gray-100 dark:border-[#1e1e24] overflow-hidden">
        {/* Top bar — title + image + stats. Layout mirrors EventHero so the
            sports page feels peer to /event: image+title row with absolute
            top-right slot for watchlist + source link. */}
        <div className="relative px-3 md:px-4 pt-3 md:pt-4 pb-2">
          <div className="flex items-stretch md:items-start gap-2.5 md:gap-4 pr-20 md:pr-24">
            {heroImage && (
              <div className="relative flex-shrink-0 self-stretch aspect-square min-w-10 min-h-10 md:w-20 md:h-20 md:self-start md:aspect-auto rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A]">
                <CdnImage src={heroImage} alt="" fill className="object-cover" onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {/* Category + context badges — replicates EventHero pill row.
                  We filter out the redundant "sports"/"esports" tags
                  (already implicit in the URL & league) and add SMART /
                  WHALE / KALSHI cross-listing badges so the sports hero
                  carries the same context signals as a normal event. */}
              {(() => {
                const tags = (fullEventData?.tags || []) as Array<string | { label?: string; slug?: string; name?: string }>;
                const mainCategories = new Set([
                  'soccer', 'tennis', 'basketball', 'football', 'baseball',
                  'hockey', 'cs2', 'lol', 'dota', 'valorant', 'mlb', 'nfl',
                  'nba', 'nhl', 'ucl', 'uefa', 'mls', 'la liga', 'premier league',
                ]);
                const labels = tags
                  .map((t) => (typeof t === 'string' ? t : (t.label || t.slug || t.name || '')))
                  .filter((l): l is string => !!l && mainCategories.has(l.toLowerCase()))
                  .slice(0, 3);
                const hasLeagueBadge = leagueName && leagueName.toUpperCase() !== 'SPORTS' && !labels.some((l) => l.toUpperCase() === leagueName.toUpperCase());
                const showAny = labels.length > 0 || hasLeagueBadge || isWhaleMarket || isSmartHeavy || hasAnyMatch;
                if (!showAny) return null;
                const kalshiList = hasExactMatch ? kalshiMatches!.exact! : (hasRelatedMatch ? kalshiMatches!.related! : []);
                const kalshiTooltip = hasAnyMatch ? [
                  hasExactMatch ? t("sportsHero.kalshiAlsoExact") : t("sportsHero.kalshiAlsoRelated"),
                  ...kalshiList.slice(0, 3).map((m) => `• ${m.title?.slice(0, 60) || m.market_ticker}`),
                  kalshiList.length > 3 ? t("sportsHero.kalshiMore", { n: kalshiList.length - 3 }) : null,
                ].filter(Boolean).join('\n') : '';
                const firstTicker = kalshiList[0]?.market_ticker;
                const kalshiHref = firstTicker ? `/kalshi/event/${extractKalshiEventTicker(firstTicker)}` : '/kalshi';
                return (
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    {/* Drop the bare sport tag (SOCCER/BASKETBALL/…) when a
                        specific league/tournament badge is shown — "FIFA WORLD
                        CUP" already implies soccer, so SOCCER is redundant. */}
                    {(hasLeagueBadge ? [] : labels).map((l) => (
                      <span
                        key={l}
                        className="inline-flex items-center h-[16px] md:h-[20px] px-1.5 md:px-2.5 rounded-[5px] text-[9px] md:text-[10px] leading-none font-bold font-inter-tight bg-blue-500 text-white"
                      >
                        {l.toUpperCase()}
                      </span>
                    ))}
                    {leagueName && leagueName.toUpperCase() !== 'SPORTS' && !labels.some((l) => l.toUpperCase() === leagueName.toUpperCase()) && (
                      <span className="inline-flex items-center h-[16px] md:h-[20px] px-1.5 md:px-2.5 rounded-[5px] text-[9px] md:text-[10px] leading-none font-bold font-inter-tight bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
                        {leagueName.toUpperCase()}
                      </span>
                    )}
                    {isWhaleMarket && (
                      <Tooltip content={t("sportsHero.whaleTooltip", { wallets: cohortRow!.wallets, vol: (cohortRow!.volume/1_000_000).toFixed(1) })}>
                        <span className="inline-flex items-center gap-1 h-[16px] md:h-[20px] px-1.5 md:px-2.5 rounded-[5px] text-[9px] md:text-[10px] leading-none font-bold font-inter-tight bg-amber-500 text-black">
                          <WhaleIcon size={11} />
                          <span className="hidden md:inline">{t("sportsEvent.whale")}</span>
                        </span>
                      </Tooltip>
                    )}
                    {isSmartHeavy && (
                      <Tooltip content={t("sportsHero.smartTooltip", { wallets: cohortRow!.wallets })}>
                        <span className="inline-flex items-center h-[16px] md:h-[20px] px-1.5 md:px-2.5 rounded-[5px] text-[9px] md:text-[10px] leading-none font-bold font-inter-tight bg-emerald-500 text-white">{t("sportsEvent.smart")}</span>
                      </Tooltip>
                    )}
                    {hasAnyMatch && (
                      <Tooltip content={kalshiTooltip}>
                        <a
                          href={kalshiHref}
                          className="inline-flex items-center gap-1 h-[16px] md:h-[20px] px-1.5 md:px-2.5 rounded-[5px] text-[9px] md:text-[10px] leading-none font-bold font-inter-tight bg-[#00D26A] text-black hover:bg-[#00b85a] transition-colors"
                        >
                          <img src="/logos/kalshi/k-icon.svg" alt="" className="w-2.5 h-2.5" />
                          KALSHI
                        </a>
                      </Tooltip>
                    )}
                  </div>
                );
              })()}
              <h1 className="text-sm md:text-xl font-monument font-bold text-gray-900 dark:text-white leading-tight break-words">
                {(teams.home?.name || teams.home?.abbreviation)} vs {(teams.away?.name || teams.away?.abbreviation)}
              </h1>
              {/* Desktop stat badges row — sits inside the title column.
                  On mobile the title column is too narrow next to the small
                  image, so we render a separate mobile-only badge row
                  outside this column (matches EventHero's pattern). */}
              {event && (
                <div className="hidden md:flex flex-wrap items-center gap-1.5 mt-1.5">
                  {tournament && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-200">
                      {tournament}
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                    {formatVolume(event.volume)} {t("sportsHero.vol")}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                    {formatVolume(event.liquidity)} {t("sportsHero.liquidity")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Mobile-only stat badges — below the image+title block, so the
              narrow title column on phones doesn't have to compete with
              tournament/Vol/Liquidity pills. Smaller text + tighter padding
              than the desktop row, mirroring EventHero. */}
          {event && (
            <div className="flex md:hidden flex-wrap items-center gap-1 mt-2">
              {tournament && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-200">
                  {tournament}
                </span>
              )}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                {formatVolume(event.volume)} {t("sportsHero.vol")}
              </span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                {formatVolume(event.liquidity)} {t("sportsHero.liquidity")}
              </span>
            </div>
          )}

          {/* Top-right: watchlist toggle + Polymarket external link.
              Absolute positioning (matches EventHero) keeps the title column
              free to wrap naturally on narrow screens. */}
          <div className="absolute top-3 right-3 md:top-4 md:right-4 inline-flex items-center gap-2 z-10">
            {eventWatchlistKey && (
              <Tooltip content={isBookmarked ? t("sportsHero.removeWatchlist") : t("sportsHero.addWatchlist")}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isLoggedIn) { login(); return; }
                    watchlistContext?.toggleWatchlist(eventWatchlistKey, event?.title || '', leaderConditionId || undefined, 'polymarket');
                  }}
                  className="flex items-center justify-center w-7 h-7 rounded-[5px] hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-gray-500 hover:text-gray-900 dark:hover:text-white"
                  aria-label={isBookmarked ? t("sportsHero.removeWatchlist") : t("sportsHero.addWatchlist")}
                >
                  <BookmarkIcon size={16} isBookmarked={isBookmarked} fillColor="#0066FF" />
                </button>
              </Tooltip>
            )}
            <a
              href={`https://polymarket.com/event/${slug}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="opacity-70 hover:opacity-100 transition-opacity"
              title={t("sportsEvent.viewOnPolymarket")}
            >
              {/* Mobile: circular badge */}
              <span className="md:hidden w-5 h-5 rounded-[5px] flex items-center justify-center bg-[#0066FF]">
                <img src="/logos/polymarket/icon-white.png" alt="PM" width={14} height={14} className="w-3.5 h-3.5 object-contain" />
              </span>
              {/* Desktop: full logo */}
              <span className="hidden md:inline-flex">
                <img src="/logos/polymarket/logo-black.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity dark:hidden" />
                <img src="/logos/polymarket/logo-white.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity hidden dark:block" />
              </span>
            </a>
          </div>
        </div>

        {/* Scheduled date/time — sits right above the team names so users
            see "30 Apr · 09:00 UTC" before the matchup. Hidden once the
            match goes live or ends. */}
        {startTimeDisplay && !game?.live && !game?.ended && (
          <div className="flex items-center justify-center px-4 -mb-1">
            <span
              title={startTimeDisplay.title}
              className="inline-flex items-center px-2.5 py-1 rounded-[5px] text-[11px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#1e1e24] border border-gray-200 dark:border-[#2a2a30] text-gray-900 dark:text-white cursor-help"
            >
              {startTimeDisplay.label}
            </span>
          </div>
        )}

        {/* Tennis: PM-style vertical scoreboard (2 rows, flag + name + set
            cells). Replaces the big center score layout below for ATP/WTA. */}
        {(game?.league === 'atp' || game?.league === 'wta') ? (
          <TennisHero
            home={{
              name: teams.home?.name || teams.home?.abbreviation || '',
              logo: teams.home?.logo,
              score: teams.home?.score,
              record: teams.home?.record,
            }}
            away={{
              name: teams.away?.name || teams.away?.abbreviation || '',
              logo: teams.away?.logo,
              score: teams.away?.score,
              record: teams.away?.record,
            }}
            rawScore={game?.score}
            period={game?.period}
            live={!!game?.live}
            ended={!!game?.ended}
            winnerHome={heroWinner === 'home'}
            winnerAway={heroWinner === 'away'}
            league={game?.league}
          />
        ) : (
        <>
        {/* Matchup — Chance.cc layout: big logos + center score */}
        <div className="relative px-6 md:px-10 lg:px-16 pt-4 pb-5 overflow-hidden">
          {/* Event background image — subtle gradient fade */}
          {event?.image && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `url(${event.image})`,
                backgroundSize: "cover",
                backgroundPosition: "top",
                maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 50%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 50%, transparent 100%)",
              }}
            />
          )}
          <div className="flex flex-col items-center max-w-3xl mx-auto gap-3">
            {/* Score row: [home logo+name] [score] [name+logo away] */}
            <div className="flex items-center justify-center w-full gap-3 md:gap-6">
              {/* Home team */}
              <div className={`flex flex-col items-center gap-1 flex-1 min-w-0 ${heroWinner && heroWinner !== 'home' ? 'opacity-40' : ''}`}>
                <div
                  className="rounded-[5px] transition-all duration-500 flex-shrink-0 p-0.5"
                  style={{
                    boxShadow: scoreFlash === "home"
                      ? "0 0 30px rgba(5,150,105,0.4), 0 0 60px rgba(5,150,105,0.15)"
                      : "none",
                  }}
                >
                  {teams.home?.logo ? (
                    <CdnImage src={teams.home.logo} alt={teams.home.name} width={96} height={96} className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] object-contain" />
                  ) : (
                    <div className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] flex items-center justify-center text-lg md:text-xl font-bold text-gray-400">
                      {teams.home?.abbreviation?.charAt(0) || "H"}
                    </div>
                  )}
                </div>
                {teams.home?.name && game?.league && hasPlayerProfilePage(game.league) ? (
                  <Link
                    href={`/sports/players/${game.league}/${playerSlug(teams.home.name)}`}
                    className="font-monument font-bold text-[11px] md:text-[13px] text-gray-900 dark:text-white truncate block max-w-[90px] md:max-w-[140px] text-center flex items-center gap-1 justify-center hover:underline underline-offset-2"
                  >
                    {teams.home?.alias || teams.home?.name || teams.home?.abbreviation}
                  </Link>
                ) : (
                  <span className="font-monument font-bold text-[11px] md:text-[13px] text-gray-900 dark:text-white truncate block max-w-[90px] md:max-w-[140px] text-center flex items-center gap-1 justify-center">
                    {teams.home?.alias || teams.home?.name || teams.home?.abbreviation}
                  </span>
                )}
              </div>

              {/* Center — score */}
              <div className="text-center flex-shrink-0">
              {/* Time & date for upcoming games. Hidden for the World Cup —
                  there's no weekly round, and the kickoff already shows in the
                  local-time badge above the teams (this block would duplicate
                  the time + add a redundant week/date). */}
              {!game.live && !game.ended && !isWorldCup && (
                <div className="mb-3">
                  <div className="flex items-center justify-center gap-2">
                    {event?.eventWeek && (
                      <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] text-[10px] font-medium text-gray-500 dark:text-gray-400">
                        {t("sportsHero.week", { n: event.eventWeek })}
                      </span>
                    )}
                    {game.time && <span className="font-monument text-[18px] text-gray-900 dark:text-white">{game.time}</span>}
                  </div>
                  {game.date && <div className="font-inter-tight text-[11px] text-gray-400 mt-0.5">{game.date}</div>}
                </div>
              )}

              {/* Score — boxed per team (winner bolder, loser dimmed),
                  same visual language used by the tennis set boxes. */}
              {game.live || game.ended ? (
                <>
                  {(() => {
                    const homeVal = displayScore.home || String(teams.home?.score ?? 0);
                    const awayVal = displayScore.away || String(teams.away?.score ?? 0);
                    const h = parseInt(homeVal, 10) || 0;
                    const a = parseInt(awayVal, 10) || 0;
                    const boxClass = (winning: boolean) =>
                      `min-w-[52px] md:min-w-[64px] px-3 py-1.5 rounded-[5px] tabular-nums text-center font-monument leading-tight transition-transform duration-300 text-[32px] md:text-[44px] lg:text-[52px] ${
                        winning
                          ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-black'
                          : 'bg-gray-50 dark:bg-[#18181D] text-gray-400 dark:text-gray-500 font-medium'
                      }`;
                    return (
                      <div className="flex items-center justify-center gap-1.5">
                        <span
                          className={boxClass(h >= a)}
                          style={{
                            color: scoreFlash === 'home' ? '#059669' : undefined,
                            transform: scoreFlash === 'home' ? 'scale(1.15)' : 'scale(1)',
                            textShadow: scoreFlash === 'home' ? '0 0 20px rgba(5,150,105,0.5)' : 'none',
                          }}
                        >
                          {homeVal}
                        </span>
                        <span className="font-monument text-[24px] md:text-[32px] lg:text-[40px] text-gray-300 dark:text-[#333] leading-none">-</span>
                        <span
                          className={boxClass(a >= h)}
                          style={{
                            color: scoreFlash === 'away' ? '#DC2626' : undefined,
                            transform: scoreFlash === 'away' ? 'scale(1.15)' : 'scale(1)',
                            textShadow: scoreFlash === 'away' ? '0 0 20px rgba(220,38,38,0.5)' : 'none',
                          }}
                        >
                          {awayVal}
                        </span>
                      </div>
                    );
                  })()}
                  {/* FINAL badge is rendered in the bottom status row — removed
                     this duplicate to avoid showing "FINAL" twice. */}
                  {(displayScore.seriesHome || period || game.format || displayScore.sets) && (
                    <div className="flex items-center justify-center gap-1.5 mt-2 flex-wrap">
                      {(() => {
                        const badges: Array<{ label: string; highlight?: boolean }> = [];

                        // Tennis sets display
                        if (displayScore.sets && displayScore.sets.length > 0) {
                          const completedSets = displayScore.sets.slice(0, -1);
                          completedSets.forEach((set, i) => {
                            badges.push({ label: t("sportsHero.setLabel", { n: i + 1, score: set }) });
                          });
                          badges.push({ label: t("sportsHero.setCurrent", { n: displayScore.sets.length }), highlight: true });
                          if (displayScore.seriesHome && displayScore.seriesAway) {
                            badges.push({ label: t("sportsHero.sets", { home: displayScore.seriesHome, away: displayScore.seriesAway }) });
                          }
                        } else {
                          // Esports / other sports
                          const periodMatch = period?.match(/^(\d+)\/(\d+)$/);
                          const fmt = displayScore.format || game.format || '';
                          const boMatch = fmt.match(/^Bo(\d+)$/i);
                          if (periodMatch) badges.push({ label: t("sportsHero.game", { n: periodMatch[1] }) });
                          if (boMatch) badges.push({ label: t("sportsHero.bestOf", { n: boMatch[1] }) });
                          else if (fmt) badges.push({ label: fmt });
                          // Esports: show current map score when series is main
                          if ((displayScore as any).mapHome != null && (displayScore as any).mapAway != null) {
                            const mh = (displayScore as any).mapHome;
                            const ma = (displayScore as any).mapAway;
                            if (mh !== '0' || ma !== '0') {
                              badges.push({ label: t("sportsHero.map", { home: mh, away: ma }), highlight: true });
                            }
                          }
                          if (displayScore.seriesHome && displayScore.seriesAway) {
                            badges.push({ label: t("sportsHero.series", { home: displayScore.seriesHome, away: displayScore.seriesAway }) });
                          }
                        }

                        // Period info — only show as badge for ended games (live shows it in LIVE badge)
                        if (!game.live && period && !period.match(/^\d+\/\d+$/) && !displayScore.sets) {
                          badges.push({ label: period });
                        }

                        // Expand abbreviated period/status labels so users see
                        // "Full Time" instead of "FT", "Half Time" vs "HT", etc.
                        // Keep the abbreviation as the hover tooltip for power users.
                        const periodKey = (k: string) => `period.${k}`;
                        const PERIOD_KEYS = new Set([
                          'FT','VFT','HT','ET','AET','PEN',
                          'Q1','Q2','Q3','Q4',
                          'OT','1H','2H',
                          'P1','P2','P3',
                        ]);

                        return badges.map((b, i) => {
                          const upper = b.label.toUpperCase();
                          const display = PERIOD_KEYS.has(upper) ? t(periodKey(upper)) : b.label;
                          return (
                          <span key={i} className={`px-2 py-0.5 rounded-[5px] text-[10px] font-medium ${
                            b.highlight
                              ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                              : "bg-gray-100 dark:bg-[#1e1e24] text-gray-500 dark:text-gray-400"
                          }`}>
                            {display}
                          </span>
                          );
                        });
                      })()}
                    </div>
                  )}
                </>
              ) : (
                <span className="font-monument text-2xl text-gray-900 dark:text-white">{t("sportsHero.vs")}</span>
              )}

              {/* Price bar inline between teams */}
              {probTotal > 0 && (
                <div className="mt-4 flex flex-col items-center gap-2">
                  {game.live && !game.ended && (
                    <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-red-500/10 text-red-500 text-[9px] font-bold rounded-[5px]">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-[5px] animate-pulse" />
                      {t("sportsHero.liveBadge")}
                      {(() => {
                        // Esports: show "Map X / N" format (period is "1/3", "2/3", etc.)
                        const esportsMatch = period?.match(/^(\d+)\/(\d+)$/);
                        if (esportsMatch) {
                          const word = (displayScore.format || game.format || '').toLowerCase().includes('game') ? t("sportsHero.gameWord") : t("sportsHero.mapWord");
                          return <span className="text-red-400">· {word} {esportsMatch[1]} / {esportsMatch[2]}</span>;
                        }
                        // Traditional sports: period + elapsed
                        if (period || game.elapsed) {
                          return <span className="text-red-400">· {[period, game.elapsed ? `${game.elapsed}'` : null].filter(Boolean).join(' - ')}</span>;
                        }
                        return null;
                      })()}
                    </span>
                  )}
                  <div className="flex items-center gap-2 justify-center w-full max-w-[280px] md:max-w-none">
                    <span className="font-monument text-[11px] md:text-[12px] text-emerald-600 dark:text-[#00FF9D] flex-shrink-0">{homePct}%</span>
                    <div className="flex h-2 md:h-2.5 rounded-[5px] overflow-hidden flex-1 md:flex-none md:w-56 lg:w-64 gap-[1px]">
                      <div className="h-full rounded-l-full bg-emerald-500 dark:bg-[#00FF9D] transition-all duration-700 ease-out" style={{ width: `${homePct}%` }} />
                      <div className="h-full rounded-r-full bg-red-500 dark:bg-[#FF1744] transition-all duration-700 ease-out" style={{ width: `${awayPct}%` }} />
                    </div>
                    <span className="font-monument text-[11px] md:text-[12px] text-red-600 dark:text-[#FF1744] flex-shrink-0">{awayPct}%</span>
                  </div>
                </div>
              )}
              {/* Watch Stream button — lifted out of the price-bar block so
                  it shows up even when moneyline prices are missing (common
                  on fresh/esports markets). Only condition now is live +
                  known streaming source (twitch/kick/youtube). */}
              {(() => {
                if (!game.live) return null;
                const streamData = getStreamData(event?.resolutionSource);
                if (!streamData) return null;
                return (
                  <div className="mt-3 flex justify-center">
                    <button
                      onClick={() => setActiveLivestream(streamData)}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-[5px] bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" className="w-3.5 h-3.5 text-red-500 animate-pulse flex-shrink-0">
                        <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor">
                          <path d="M5.641,12.359c-1.855-1.855-1.855-4.863,0-6.718" />
                          <path d="M12.359,5.641c1.855,1.855,1.855,4.863,0,6.718" />
                          <circle cx="9" cy="9" r="2" fill="currentColor" />
                          <path d="M3.52,14.48C.493,11.454,.493,6.546,3.52,3.52" opacity="0.5" />
                          <path d="M14.48,14.48c3.027-3.027,3.027-7.934,0-10.96" opacity="0.5" />
                        </g>
                      </svg>
                      <span className="text-[11px] font-medium text-red-600 dark:text-red-400">{t("sportsEvent.watchStream")}</span>
                    </button>
                  </div>
                );
              })()}
            </div>

              {/* Away team */}
              <div className={`flex flex-col items-center gap-1 flex-1 min-w-0 ${heroWinner && heroWinner !== 'away' ? 'opacity-40' : ''}`}>
                <div
                  className="rounded-[5px] transition-all duration-500 flex-shrink-0 p-0.5"
                  style={{
                    boxShadow: scoreFlash === "away"
                      ? "0 0 30px rgba(220,38,38,0.4), 0 0 60px rgba(220,38,38,0.15)"
                      : "none",
                  }}
                >
                  {teams.away?.logo ? (
                    <CdnImage src={teams.away.logo} alt={teams.away.name} width={96} height={96} className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] object-contain" />
                  ) : (
                    <div className="w-10 h-10 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-[5px] bg-gray-100 dark:bg-[#1e1e24] flex items-center justify-center text-lg md:text-xl font-bold text-gray-400">
                      {teams.away?.abbreviation?.charAt(0) || "A"}
                    </div>
                  )}
                </div>
                {teams.away?.name && game?.league && hasPlayerProfilePage(game.league) ? (
                  <Link
                    href={`/sports/players/${game.league}/${playerSlug(teams.away.name)}`}
                    className="font-monument font-bold text-[11px] md:text-[13px] text-gray-900 dark:text-white truncate block max-w-[90px] md:max-w-[140px] text-center flex items-center gap-1 justify-center hover:underline underline-offset-2"
                  >
                    {teams.away?.alias || teams.away?.name || teams.away?.abbreviation}
                  </Link>
                ) : (
                  <span className="font-monument font-bold text-[11px] md:text-[13px] text-gray-900 dark:text-white truncate block max-w-[90px] md:max-w-[140px] text-center flex items-center gap-1 justify-center">
                    {teams.away?.alias || teams.away?.name || teams.away?.abbreviation}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        </>
        )}

        {/* Final status pill — only when match has ended. The Live state
            already lives in the Watch Stream row above (red dot + Live)
            so we don't re-render it here, only Final lands as a pill. */}
        {game.ended && (
          <div className="flex items-center justify-center gap-2 px-4 pb-2.5 flex-wrap">
            <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30 text-[10px] font-bold uppercase tracking-wider">
              {t("sportsEvent.final")}
            </span>
          </div>
        )}

        {/* Chart — Multi-outcome (same as regular event pages) */}
        {chartMarkets.length >= 2 ? (
          <div className="border-t border-gray-100 dark:border-[#1e1e24]">
            <EventMultiOutcomeChart
              markets={chartMarkets}
              eventTitle={event?.title || ''}
              source="polymarket"
              eventSlug={slug}
              // World Cup matches haven't started → 1H is empty; default to the
              // full pre-tournament history.
              defaultTimeFilter={isWorldCup ? "ALL" : "1H"}
            />
          </div>
        ) : null}

        {/* ── MARKET LINES — Tabs (like Polymarket).
             Reads the same `displayByType` we built at component scope so
             selectedMarketIdx and the right-column trading panel stay in
             sync with the visible tabs (especially the merged 1st Set
             tab whose key doesn't exist in raw marketsByType). */}
        {displayByType && (() => {
          const tabs = Object.entries(displayByType);
          const activeTab = tabs.find(([t]) => t === activeMarketType) ? activeMarketType : tabs[0]?.[0];
          const activeGroup = displayByType[activeTab];

          return (
            <div className="border-t border-gray-100 dark:border-[#1e1e24]">
              {/* Tab bar */}
              <div className="flex overflow-x-auto touch-pan-x [-webkit-overflow-scrolling:touch] bg-gray-50 dark:bg-[#0f0f12] border-b border-gray-200 dark:border-[#1A1A1A] scrollbar-hide">
                {tabs.map(([type, group]) => (
                  <button
                    key={type}
                    onClick={() => { setActiveMarketType(type); setSelectedMarketIdx(0); setSelectedOutcomeIdx(0); }}
                    className={`px-3 py-2 text-[10px] font-inter-tight font-bold uppercase tracking-wider whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${
                      activeTab === type
                        ? "text-gray-900 dark:text-white border-gray-900 dark:border-white"
                        : "text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    {group.label}
                  </button>
                ))}
              </div>

              {/* Active tab content */}
              {activeGroup && (
                <div>
                  {(() => {
                    const childMoneylines = activeGroup.markets
                      .map((m: any, i: number) => ({ m, i }))
                      .filter(({ m }: any) => m.sportsMarketType === 'child_moneyline');
                    let childRendered = false;
                    // Game Handicap pairs (TT/LGD with the same magnitude
                    // line) collapse into a single compact row with chip
                    // selector — same pattern as Game Winner above.
                    // Spread / handicap markets — `map_handicap` for
                    // esports, `spreads` for MLB / NFL / NBA. Same
                    // collapse-into-one-row-with-chip-selector pattern.
                    const handicapMarkets = activeGroup.markets
                      .map((m: any, i: number) => ({ m, i }))
                      .filter(({ m }: any) => m.sportsMarketType === 'map_handicap' || m.sportsMarketType === 'spreads');
                    let handicapRendered = false;
                    // Kill Totals — many over/under lines (16.5..32.5)
                    // collapse into a single row with a horizontally
                    // scrolling line selector. Same pattern as Game
                    // Winner / Game Handicap above.
                    const killTotalMarkets = activeGroup.markets
                      .map((m: any, i: number) => ({ m, i }))
                      .filter(({ m }: any) => m.sportsMarketType === 'kill_over_under_game')
                      .sort((a, b) => (Number(a.m.line) || 0) - (Number(b.m.line) || 0));
                    let killTotalRendered = false;
                    // Generic Totals (MLB run line totals, soccer goals,
                    // etc.) — same line-selector pattern as Kill Totals.
                    const totalMarkets = activeGroup.markets
                      .map((m: any, i: number) => ({ m, i }))
                      .filter(({ m }: any) => m.sportsMarketType === 'totals')
                      .sort((a, b) => (Number(a.m.line) || 0) - (Number(b.m.line) || 0));
                    let totalRendered = false;

                    // Sub-section header offsets — for merged tabs (1st Set)
                    // we insert a small "1st Set Winner" / "1st Set Total
                    // Games" header before the first row of each section.
                    const sectionStarts: Record<number, { label: string; vol: number }> = {};
                    if (activeGroup.subSections) {
                      let acc = 0;
                      for (const s of activeGroup.subSections) {
                        const vol = (s.markets as any[]).reduce((sum, m) => sum + (Number(m.volume) || 0), 0);
                        sectionStarts[acc] = { label: s.label, vol };
                        acc += s.markets.length;
                      }
                    }

                    return activeGroup.markets.map((market: any, mIdx: number) => {
                      const sectionHead = sectionStarts[mIdx];
                      // Render child_moneyline markets (Game N Winner) as a
                      // SINGLE compact row with a 1/2/3 map selector — same
                      // collapsed layout we had before the per-row split.
                      if (market.sportsMarketType === 'child_moneyline') {
                        if (childRendered) return null;
                        childRendered = true;
                        const selectedChildIdx = childMoneylines.findIndex(({ i }: any) => activeTab === activeMarketType && selectedMarketIdx === i);
                        const activeChildLocal = selectedChildIdx >= 0 ? selectedChildIdx : 0;
                        const activeChild = childMoneylines[activeChildLocal].m;
                        const activeMIdx = childMoneylines[activeChildLocal].i;
                        const isSelectedChild = activeTab === activeMarketType && childMoneylines.some(({ i }: any) => selectedMarketIdx === i);
                        const isGameWord = (activeChild.groupItemTitle || '').toLowerCase().includes('game');
                        return (
                          <div
                            key="child-moneyline-group"
                            onClick={() => {
                              setActiveMarketType(activeTab);
                              setSelectedMarketIdx(activeMIdx);
                              setSelectedOutcomeIdx(0);
                              setTradingFlash(true);
                              setTimeout(() => setTradingFlash(false), 600);
                            }}
                            className={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 px-3 md:px-4 py-2.5 md:py-3 border-t border-gray-100 dark:border-[#1e1e24] first:border-t-0 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30 ${
                              isSelectedChild ? "bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600" : ""
                            }`}
                          >
                            <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                              <Award className="w-5 h-5" aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white">
                                  {t(isGameWord ? "marketLabel.gameNWinner" : "marketLabel.mapNWinner", { n: activeChildLocal + 1 })}
                                </span>
                                <div className="flex gap-0.5" onClick={e => e.stopPropagation()}>
                                  {childMoneylines.map(({ i }: any, cIdx: number) => (
                                    <button
                                      key={cIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMarketType(activeTab);
                                        setSelectedMarketIdx(i);
                                        setSelectedOutcomeIdx(0);
                                        setTradingFlash(true);
                                        setTimeout(() => setTradingFlash(false), 600);
                                      }}
                                      className={`w-6 h-6 text-[11px] font-semibold font-inter-tight rounded-[5px] transition-colors ${
                                        activeChildLocal === cIdx
                                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                                          : "bg-gray-100 dark:bg-[#1e1e24] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                      }`}
                                    >
                                      {cIdx + 1}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                                {formatVolume(activeChild.volume)} {t("chartMeta.volSuffix")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 self-center w-full md:w-auto justify-end order-3 md:order-none">
                              {activeChild.outcomes.slice(0, 2).map((outcome: string, oIdx: number) => {
                                const isActiveBtn = isSelectedChild && selectedOutcomeIdx === oIdx && selectedMarketIdx === activeMIdx;
                                const isYesSide = oIdx === 0;
                                const tokenId = activeChild.clobTokenIds[oIdx];
                                const p = (tokenId ? allLivePrices[tokenId] : undefined) ?? activeChild.outcomePrices[oIdx] ?? 0;
                                const priceCents = formatPrice(p).replace('¢', '');
                                const bgClass = isYesSide
                                  ? isActiveBtn ? "bg-[#359a5e] hover:bg-[#2d8450]" : "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                                  : isActiveBtn ? "bg-[#cb3131] hover:bg-[#b82a2a]" : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25";
                                const labelClass = isYesSide
                                  ? isActiveBtn ? "text-white/80" : "text-[#3db468]/80"
                                  : isActiveBtn ? "text-white/80" : "text-[#cb3131]/80";
                                const priceClass = isYesSide
                                  ? isActiveBtn ? "text-white" : "text-[#3db468]"
                                  : isActiveBtn ? "text-white" : "text-[#cb3131]";
                                const teamAbbrLookup = (() => {
                                  const oLower = (outcome || '').toLowerCase();
                                  for (const t of [teams?.home, teams?.away]) {
                                    if (!t?.name) continue;
                                    if (oLower.includes(t.name.toLowerCase()) && t.abbreviation) return String(t.abbreviation).toUpperCase();
                                  }
                                  return outcome.toUpperCase();
                                })();
                                return (
                                  <button
                                    key={oIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarketType(activeTab);
                                      setSelectedMarketIdx(activeMIdx);
                                      setSelectedOutcomeIdx(oIdx);
                                      setTradingFlash(true);
                                      setTimeout(() => setTradingFlash(false), 600);
                                    }}
                                    className={`inline-flex items-center justify-center gap-1.5 flex-1 md:flex-none px-4 py-2.5 rounded-[5px] transition-colors whitespace-nowrap ${bgClass}`}
                                  >
                                    <span className={`text-xs font-inter-tight font-bold uppercase ${labelClass}`}>{teamAbbrLookup}</span>
                                    <span className={`text-sm font-inter-tight font-bold tabular-nums ${priceClass}`}>{priceCents}¢</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      // Render map_handicap markets (e.g. -1.5 each direction)
                      // as a SINGLE compact row with chip selector — mirrors
                      // PM's Series Lines layout. Subsequent handicap rows
                      // are skipped so we don't duplicate.
                      if ((market.sportsMarketType === 'map_handicap' || market.sportsMarketType === 'spreads') && handicapMarkets.length > 1) {
                        if (handicapRendered) return null;
                        handicapRendered = true;
                        const selectedHandIdx = handicapMarkets.findIndex(({ i }: any) => activeTab === activeMarketType && selectedMarketIdx === i);
                        const activeHandLocal = selectedHandIdx >= 0 ? selectedHandIdx : 0;
                        const activeHand = handicapMarkets[activeHandLocal].m;
                        const activeHandMIdx = handicapMarkets[activeHandLocal].i;
                        const isSelectedHand = activeTab === activeMarketType && handicapMarkets.some(({ i }: any) => selectedMarketIdx === i);
                        return (
                          <div
                            key="handicap-group"
                            onClick={() => {
                              setActiveMarketType(activeTab);
                              setSelectedMarketIdx(activeHandMIdx);
                              setSelectedOutcomeIdx(0);
                              setTradingFlash(true);
                              setTimeout(() => setTradingFlash(false), 600);
                            }}
                            className={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 px-3 md:px-4 py-2.5 md:py-3 border-t border-gray-100 dark:border-[#1e1e24] first:border-t-0 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30 ${
                              isSelectedHand ? "bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600" : ""
                            }`}
                          >
                            <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                              <Sliders className="w-5 h-5" aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white">
                                  {market.sportsMarketType === 'spreads' ? t("sportsTrade.spread") : (isMapsLeague(game?.league) ? t("sportsTrade.mapHandicap") : t("sportsTrade.gameHandicap"))}
                                </span>
                                <div className="flex gap-0.5" onClick={e => e.stopPropagation()}>
                                  {handicapMarkets.map(({ m, i }: any, hIdx: number) => (
                                    <button
                                      key={hIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMarketType(activeTab);
                                        setSelectedMarketIdx(i);
                                        setSelectedOutcomeIdx(0);
                                        setTradingFlash(true);
                                        setTimeout(() => setTradingFlash(false), 600);
                                      }}
                                      className={`px-1.5 h-6 text-[11px] font-semibold font-inter-tight rounded-[5px] transition-colors tabular-nums ${
                                        activeHandLocal === hIdx
                                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                                          : "bg-gray-100 dark:bg-[#1e1e24] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                      }`}
                                    >
                                      {m.line != null ? (m.line > 0 ? `+${m.line}` : m.line) : hIdx + 1}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                                {formatVolume(activeHand.volume)} {t("chartMeta.volSuffix")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 self-center w-full md:w-auto justify-end order-3 md:order-none">
                              {activeHand.outcomes.slice(0, 2).map((outcome: string, oIdx: number) => {
                                const isActiveBtn = isSelectedHand && selectedOutcomeIdx === oIdx && selectedMarketIdx === activeHandMIdx;
                                const isYesSide = oIdx === 0;
                                const tokenId = activeHand.clobTokenIds[oIdx];
                                const p = (tokenId ? allLivePrices[tokenId] : undefined) ?? activeHand.outcomePrices[oIdx] ?? 0;
                                const priceCents = formatPrice(p).replace('¢', '');
                                const bgClass = isYesSide
                                  ? isActiveBtn ? "bg-[#359a5e] hover:bg-[#2d8450]" : "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                                  : isActiveBtn ? "bg-[#cb3131] hover:bg-[#b82a2a]" : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25";
                                const labelClass = isYesSide
                                  ? isActiveBtn ? "text-white/80" : "text-[#3db468]/80"
                                  : isActiveBtn ? "text-white/80" : "text-[#cb3131]/80";
                                const priceClass = isYesSide
                                  ? isActiveBtn ? "text-white" : "text-[#3db468]"
                                  : isActiveBtn ? "text-white" : "text-[#cb3131]";
                                const teamAbbrLookup = (() => {
                                  const oLower = (outcome || '').toLowerCase();
                                  for (const t of [teams?.home, teams?.away]) {
                                    if (!t?.name) continue;
                                    if (oLower.includes(t.name.toLowerCase()) && t.abbreviation) return String(t.abbreviation).toUpperCase();
                                  }
                                  return outcome.toUpperCase();
                                })();
                                return (
                                  <button
                                    key={oIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarketType(activeTab);
                                      setSelectedMarketIdx(activeHandMIdx);
                                      setSelectedOutcomeIdx(oIdx);
                                      setTradingFlash(true);
                                      setTimeout(() => setTradingFlash(false), 600);
                                    }}
                                    className={`inline-flex items-center justify-center gap-1.5 flex-1 md:flex-none px-4 py-2.5 rounded-[5px] transition-colors whitespace-nowrap ${bgClass}`}
                                  >
                                    <span className={`text-xs font-inter-tight font-bold uppercase ${labelClass}`}>{teamAbbrLookup}</span>
                                    <span className={`text-sm font-inter-tight font-bold tabular-nums ${priceClass}`}>{priceCents}¢</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      // Kill Totals (kill_over_under_game) — many lines
                      // collapse into one row with a scrollable line
                      // selector + the active line's Over/Under buttons.
                      if (market.sportsMarketType === 'kill_over_under_game' && killTotalMarkets.length > 1) {
                        if (killTotalRendered) return null;
                        killTotalRendered = true;
                        const selectedKtIdx = killTotalMarkets.findIndex(({ i }: any) => activeTab === activeMarketType && selectedMarketIdx === i);
                        const activeKtLocal = selectedKtIdx >= 0 ? selectedKtIdx : 0;
                        const activeKt = killTotalMarkets[activeKtLocal].m;
                        const activeKtMIdx = killTotalMarkets[activeKtLocal].i;
                        const isSelectedKt = activeTab === activeMarketType && killTotalMarkets.some(({ i }: any) => selectedMarketIdx === i);
                        return (
                          <div
                            key="kill-totals-group"
                            onClick={() => {
                              setActiveMarketType(activeTab);
                              setSelectedMarketIdx(activeKtMIdx);
                              setSelectedOutcomeIdx(0);
                              setTradingFlash(true);
                              setTimeout(() => setTradingFlash(false), 600);
                            }}
                            className={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 px-3 md:px-4 py-2.5 md:py-3 border-t border-gray-100 dark:border-[#1e1e24] first:border-t-0 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30 ${
                              isSelectedKt ? "bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600" : ""
                            }`}
                          >
                            <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                              <Swords className="w-5 h-5" aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white flex-shrink-0">
                                  {t("sportsEvent.killTotals")}
                                </span>
                                {/* Line selector inline next to the title.
                                    Drag-to-scroll on desktop, native pan
                                    on mobile so users can scrub through
                                    all 17 lines without a scrollbar. */}
                                <DragScrollRow
                                  className="flex gap-1 overflow-x-auto scrollbar-hide [-webkit-overflow-scrolling:touch] touch-pan-x flex-1 min-w-0"
                                  onClickInside={e => e.stopPropagation()}
                                >
                                  {killTotalMarkets.map(({ m, i }: any, kIdx: number) => (
                                    <button
                                      key={kIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMarketType(activeTab);
                                        setSelectedMarketIdx(i);
                                        setSelectedOutcomeIdx(0);
                                        setTradingFlash(true);
                                        setTimeout(() => setTradingFlash(false), 600);
                                      }}
                                      className={`flex-shrink-0 px-2 h-6 text-[11px] font-semibold font-inter-tight rounded-[5px] transition-colors tabular-nums ${
                                        activeKtLocal === kIdx
                                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                                          : "bg-gray-100 dark:bg-[#1e1e24] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                      }`}
                                    >
                                      {m.line ?? '?'}
                                    </button>
                                  ))}
                                </DragScrollRow>
                              </div>
                              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                                {formatVolume(killTotalMarkets.reduce((s: number, { m }: any) => s + (Number(m.volume) || 0), 0))} {t("chartMeta.volSuffix")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 self-center w-full md:w-auto justify-end order-3 md:order-none">
                              {activeKt.outcomes.slice(0, 2).map((outcome: string, oIdx: number) => {
                                const isActiveBtn = isSelectedKt && selectedOutcomeIdx === oIdx && selectedMarketIdx === activeKtMIdx;
                                const isYesSide = oIdx === 0;
                                const tokenId = activeKt.clobTokenIds[oIdx];
                                const p = (tokenId ? allLivePrices[tokenId] : undefined) ?? activeKt.outcomePrices[oIdx] ?? 0;
                                const priceCents = formatPrice(p).replace('¢', '');
                                const bgClass = isYesSide
                                  ? isActiveBtn ? "bg-[#359a5e] hover:bg-[#2d8450]" : "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                                  : isActiveBtn ? "bg-[#cb3131] hover:bg-[#b82a2a]" : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25";
                                const labelClass = isYesSide
                                  ? isActiveBtn ? "text-white/80" : "text-[#3db468]/80"
                                  : isActiveBtn ? "text-white/80" : "text-[#cb3131]/80";
                                const priceClass = isYesSide
                                  ? isActiveBtn ? "text-white" : "text-[#3db468]"
                                  : isActiveBtn ? "text-white" : "text-[#cb3131]";
                                const ouLabel = (() => {
                                  const oLower = (outcome || '').toLowerCase();
                                  if (oLower === 'over' || oLower.startsWith('over ')) return 'O';
                                  if (oLower === 'under' || oLower.startsWith('under ')) return 'U';
                                  return outcome.toUpperCase();
                                })();
                                return (
                                  <button
                                    key={oIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarketType(activeTab);
                                      setSelectedMarketIdx(activeKtMIdx);
                                      setSelectedOutcomeIdx(oIdx);
                                      setTradingFlash(true);
                                      setTimeout(() => setTradingFlash(false), 600);
                                    }}
                                    className={`inline-flex items-center justify-center gap-1.5 flex-1 md:flex-none px-4 py-2.5 rounded-[5px] transition-colors whitespace-nowrap ${bgClass}`}
                                  >
                                    <span className={`text-xs font-inter-tight font-bold uppercase ${labelClass}`}>{ouLabel} {activeKt.line ?? ''}</span>
                                    <span className={`text-sm font-inter-tight font-bold tabular-nums ${priceClass}`}>{priceCents}¢</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      // Generic Totals — multiple Over/Under lines (8.5,
                      // 9.5, 10.5 …) collapse into one row with a chip
                      // selector. Same compact layout as Kill Totals.
                      if (market.sportsMarketType === 'totals' && totalMarkets.length > 1) {
                        if (totalRendered) return null;
                        totalRendered = true;
                        const selectedToIdx = totalMarkets.findIndex(({ i }: any) => activeTab === activeMarketType && selectedMarketIdx === i);
                        const activeToLocal = selectedToIdx >= 0 ? selectedToIdx : 0;
                        const activeTo = totalMarkets[activeToLocal].m;
                        const activeToMIdx = totalMarkets[activeToLocal].i;
                        const isSelectedTo = activeTab === activeMarketType && totalMarkets.some(({ i }: any) => selectedMarketIdx === i);
                        const totalsTotalVol = totalMarkets.reduce((s: number, { m }: any) => s + (Number(m.volume) || 0), 0);
                        return (
                          <div
                            key="totals-group"
                            onClick={() => {
                              setActiveMarketType(activeTab);
                              setSelectedMarketIdx(activeToMIdx);
                              setSelectedOutcomeIdx(0);
                              setTradingFlash(true);
                              setTimeout(() => setTradingFlash(false), 600);
                            }}
                            className={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 px-3 md:px-4 py-2.5 md:py-3 border-t border-gray-100 dark:border-[#1e1e24] first:border-t-0 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30 ${
                              isSelectedTo ? "bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600" : ""
                            }`}
                          >
                            <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                              <ArrowUpDown className="w-5 h-5" aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white flex-shrink-0">
                                  {t("sportsEvent.totalGames")}
                                </span>
                                <DragScrollRow
                                  className="flex gap-1 overflow-x-auto scrollbar-hide [-webkit-overflow-scrolling:touch] touch-pan-x flex-1 min-w-0"
                                  onClickInside={e => e.stopPropagation()}
                                >
                                  {totalMarkets.map(({ m, i }: any, tIdx: number) => (
                                    <button
                                      key={tIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMarketType(activeTab);
                                        setSelectedMarketIdx(i);
                                        setSelectedOutcomeIdx(0);
                                        setTradingFlash(true);
                                        setTimeout(() => setTradingFlash(false), 600);
                                      }}
                                      className={`flex-shrink-0 px-2 h-6 text-[11px] font-semibold font-inter-tight rounded-[5px] transition-colors tabular-nums ${
                                        activeToLocal === tIdx
                                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                                          : "bg-gray-100 dark:bg-[#1e1e24] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                      }`}
                                    >
                                      {m.line ?? '?'}
                                    </button>
                                  ))}
                                </DragScrollRow>
                              </div>
                              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                                {formatVolume(totalsTotalVol)} {t("chartMeta.volSuffix")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 self-center w-full md:w-auto justify-end order-3 md:order-none">
                              {activeTo.outcomes.slice(0, 2).map((outcome: string, oIdx: number) => {
                                const isActiveBtn = isSelectedTo && selectedOutcomeIdx === oIdx && selectedMarketIdx === activeToMIdx;
                                const isYesSide = oIdx === 0;
                                const tokenId = activeTo.clobTokenIds[oIdx];
                                const p = (tokenId ? allLivePrices[tokenId] : undefined) ?? activeTo.outcomePrices[oIdx] ?? 0;
                                const priceCents = formatPrice(p).replace('¢', '');
                                const bgClass = isYesSide
                                  ? isActiveBtn ? "bg-[#359a5e] hover:bg-[#2d8450]" : "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                                  : isActiveBtn ? "bg-[#cb3131] hover:bg-[#b82a2a]" : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25";
                                const labelClass = isYesSide
                                  ? isActiveBtn ? "text-white/80" : "text-[#3db468]/80"
                                  : isActiveBtn ? "text-white/80" : "text-[#cb3131]/80";
                                const priceClass = isYesSide
                                  ? isActiveBtn ? "text-white" : "text-[#3db468]"
                                  : isActiveBtn ? "text-white" : "text-[#cb3131]";
                                const ouLabel = (() => {
                                  const oLower = (outcome || '').toLowerCase();
                                  if (oLower === 'over' || oLower.startsWith('over ')) return 'O';
                                  if (oLower === 'under' || oLower.startsWith('under ')) return 'U';
                                  return outcome.toUpperCase();
                                })();
                                return (
                                  <button
                                    key={oIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarketType(activeTab);
                                      setSelectedMarketIdx(activeToMIdx);
                                      setSelectedOutcomeIdx(oIdx);
                                      setTradingFlash(true);
                                      setTimeout(() => setTradingFlash(false), 600);
                                    }}
                                    className={`inline-flex items-center justify-center gap-1.5 flex-1 md:flex-none px-4 py-2.5 rounded-[5px] transition-colors whitespace-nowrap ${bgClass}`}
                                  >
                                    <span className={`text-xs font-inter-tight font-bold uppercase ${labelClass}`}>{ouLabel} {activeTo.line ?? ''}</span>
                                    <span className={`text-sm font-inter-tight font-bold tabular-nums ${priceClass}`}>{priceCents}¢</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                      const isSelected = activeTab === activeMarketType && selectedMarketIdx === mIdx;
                      // Row-level team badge (embed style): match the market
                      // to a team via the ORIGINAL groupItemTitle/question —
                      // outcomes[0] is usually "Yes" for neg-risk rows and
                      // doesn't carry the team name.
                      const rowLabelRaw = cleanMarketLabel(market, activeGroup?.markets?.length, game?.league, t);
                      const rowLabel = shortenTitleWithAliases(rowLabelRaw, teams?.home, teams?.away);
                      const rowLabelIsDraw = /^\s*Draw\b/i.test(rowLabel);
                      // Exact-score rows ("2-1", "Exact Score: 2-1", etc.) —
                      // the label *contains* a team name sometimes (depending
                      // on Polymarket phrasing), but the market is not team-
                      // specific, so we render a neutral Target icon instead
                      // of fuzzy-matching to whichever team happens to be
                      // named in the question.
                      const rowLabelIsExactScore = /\d+\s*[-–:]\s*\d+/.test(rowLabel) || /exact\s*score/i.test(market.question || '');
                      // Player-level markets (goalscorer, first scorer, etc.)
                      // — keep team logo OUT since these rows are about a
                      // specific player, not the team as a whole.
                      const rowLabelIsGoalscorer = /goalscorer|to\s+score|first\s+scorer|anytime\s+scorer/i.test(`${market.groupItemTitle || ''} ${rowLabelRaw}`);
                      const rowLabelIsCorners = market.sportsMarketType === 'total_corners' || /\bcorner/i.test(market.question || '');
                      const matchText = `${market.groupItemTitle || ''} ${market.question || ''}`.toLowerCase();
                      const matchesRowTeam = (t?: Team) => {
                        if (!t) return false;
                        const c = [t.alias, t.name, t.abbreviation].filter(Boolean).map(s => String(s).toLowerCase()).filter(s => s.length >= 3);
                        return c.some(v => matchText.includes(v));
                      };
                      // Generic two-way moneyline-style markets (Moneyline,
                      // Set Winner, Match Winner) describe BOTH players, so
                      // showing one player's flag/logo is misleading. Suppress
                      // the badge entirely on those rows — outcome buttons
                      // already carry the player surname.
                      const rowLabelIsGenericTwoWay = /^(moneyline|match\s*winner|set\s*\d*\s*winner|game\s*winner|map\s*winner)$/i.test(rowLabel);
                      // Handicap / spread markets — no team logo. Icon
                      // is the spread "balance" sliders.
                      const rowLabelIsHandicap = market.sportsMarketType === 'map_handicap' || /handicap|spread/i.test(rowLabel);
                      // First Blood — esports event-specific, no team logo.
                      const rowLabelIsFirstBlood = market.sportsMarketType === 'first_blood_game';
                      // NRFI / YRFI — "No / Yes Run First Inning" baseball
                      // markets. Both teams equally implicated, no team
                      // logo applies; use the Hash icon (1st inning).
                      const rowLabelIsNrfi = market.sportsMarketType === 'nrfi' || market.sportsMarketType === 'yrfi';
                      // LoL "Both Teams …" markets — both teams equally
                      // involved, no single team logo.
                      const rowLabelIsBothTeams = /^(lol_both_teams_|lol_odd_even|lol_quadra_kill|lol_penta_kill)/.test(market.sportsMarketType || '');
                      // Set/Game/Map winners are sub-event winners (vs Match
                      // Winner = the whole match) — use Award to distinguish
                      // them from the trophy on the main Moneyline row.
                      const rowLabelIsSubWinner = /^(set\s*\d*\s*winner|game\s*winner|map\s*winner)$/i.test(rowLabel);
                      // Over/under totals markets (Match Totals, Set Totals,
                      // First Set Totals) — neutral about which player wins,
                      // it's about combined games. Use ArrowUpDown like the
                      // Discord-activity embed does for o/u markets.
                      const rowLabelIsTotals = /\btotals?\b|\bover\/under\b|\bo\/u\b/i.test(rowLabel) || /total/i.test(market.sportsMarketType || '');
                      // Collapse a verbose totals label like "Saint-Malo: Y.
                      // Kabbaj vs J. Ponchet Set 1 O/U 9.5" to "Total 9.5
                      // games" — tab + outcome buttons already carry the
                      // tournament/players/set context. Unit follows the
                      // sport so the row reads naturally.
                      const totalsUnit = (() => {
                        const lg = (game?.league || '').toLowerCase();
                        if (lg === 'atp' || lg === 'wta') return 'games';
                        if (lg === 'mlb') return 'runs';
                        if (lg === 'nhl') return 'goals';
                        if (['nba','nfl','ncaab','ncaaf'].includes(lg)) return 'points';
                        if (['epl','laliga','ucl','uefa','mls','seriea','bundesliga'].includes(lg)) return 'goals';
                        // Esports series-level totals — PM labels these
                        // "Total Games" (the count of games inside the
                        // best-of series). The +/- line badge to the
                        // right of the label carries the actual line.
                        if (market.sportsMarketType === 'totals') return 'games';
                        return null;
                      })();
                      const finalRowLabel = rowLabelIsTotals && market.line != null
                        ? totalsUnit
                          // Series-level totals on esports use "Maps" for
                          // CS2/Valorant, "Games" for LoL/Dota — see
                          // MAPS_LEAGUES. Map-specific totals (kill totals,
                          // etc.) keep their existing unit.
                          ? (market.sportsMarketType === 'totals'
                              ? (isMapsLeague(game?.league) ? t("marketLabel.totalMaps") : t("marketLabel.totalGames"))
                              : t("marketLabel.totalLineUnit", { n: market.line, unit: t(`marketLabel.unit.${totalsUnit}` as any) || totalsUnit }))
                          : t("marketLabel.totalLine", { n: market.line })
                        : (rowLabelIsDraw ? t("sportsMatch.draw") : rowLabel);
                      const rowTeamLogo = rowLabelIsDraw || rowLabelIsExactScore || rowLabelIsGoalscorer || rowLabelIsCorners || rowLabelIsGenericTwoWay || rowLabelIsTotals || rowLabelIsHandicap || rowLabelIsFirstBlood || rowLabelIsBothTeams || rowLabelIsNrfi
                        ? null
                        : matchesRowTeam(teams?.home) ? teams?.home?.logo
                        : matchesRowTeam(teams?.away) ? teams?.away?.logo
                        : null;
                      return (
                      <React.Fragment key={market.id}>
                        {sectionHead && (
                          <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-gray-100 dark:border-[#1e1e24] bg-gray-50/50 dark:bg-[#0c0c10]/40 first:border-t-0">
                            <span className="font-inter-tight font-semibold text-[12px] text-gray-700 dark:text-gray-200">
                              {sectionHead.label}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                              {formatVolume(sectionHead.vol)} {t("chartMeta.volSuffix")}
                            </span>
                          </div>
                        )}
                      <div
                        onClick={() => {
                          setActiveMarketType(activeTab);
                          setSelectedMarketIdx(mIdx);
                          setSelectedOutcomeIdx(0);
                          setTradingFlash(true);
                          setTimeout(() => setTradingFlash(false), 600);
                        }}
                        className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 border-t border-gray-100 dark:border-[#1e1e24] first:border-t-0 transition-colors cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12]/30 ${
                          isSelected ? "bg-gray-100 dark:bg-[#26262D] border-l-2 border-l-gray-300 dark:border-l-zinc-600" : ""
                        }`}
                      >
                        {/* Team crest / Draw handshake — same pattern as the
                            Discord activity embed: big 28px badge anchors the
                            row so the user can scan markets by team. */}
                        {rowLabelIsDraw ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Handshake className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsExactScore ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Target className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsGoalscorer ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <User className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsCorners ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Flag className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsGenericTwoWay ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            {rowLabelIsSubWinner ? <Award className="w-5 h-5" aria-hidden="true" /> : <Trophy className="w-5 h-5" aria-hidden="true" />}
                          </div>
                        ) : rowLabelIsTotals ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <ArrowUpDown className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsHandicap ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Sliders className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsFirstBlood ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Swords className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowLabelIsNrfi ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Hash className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'lol_both_teams_baron' || market.sportsMarketType === 'dota2_both_teams_roshan' ? (
                          // Roshan in Dota2 ≈ Baron in LoL — boss-monster
                          // objective. Reuse Skull for both.
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Skull className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'lol_both_teams_dragon' ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Flame className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'lol_both_teams_inhibitors' || market.sportsMarketType === 'dota2_both_teams_barracks' ? (
                          // Barracks in Dota2 ≈ Inhibitors in LoL — base
                          // structure that drops super-creeps when destroyed.
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Shield className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'lol_quadra_kill' || market.sportsMarketType === 'lol_penta_kill' || market.sportsMarketType === 'dota2_rampage' || market.sportsMarketType === 'dota2_ultra_kill' ? (
                          // Streak achievements (Quadra/Penta in LoL,
                          // Ultra Kill / Rampage in Dota2).
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Star className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'dota2_game_ends_daytime' ? (
                          // "Will the game end in daytime?" — pure Dota2.
                          // Closest visual is a sun/light glyph; Zap reads
                          // as "energy / phase" without needing a sun icon.
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Zap className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : market.sportsMarketType === 'lol_odd_even_total_kills' ? (
                          <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center text-gray-600 dark:text-gray-400 flex-shrink-0">
                            <Hash className="w-5 h-5" aria-hidden="true" />
                          </div>
                        ) : rowTeamLogo ? (
                          <CdnImage src={rowTeamLogo} alt="" width={40} height={40} className="w-10 h-10 object-contain flex-shrink-0" />
                        ) : null}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-inter-tight font-semibold text-sm text-gray-900 dark:text-white truncate">
                              {finalRowLabel}
                            </span>
                            {/* Hide the +/- line badge when the row label
                                already spells out the line (corners totals,
                                over/under rows) — avoids "+10.5" appearing
                                twice on the same row. */}
                            {market.line != null && !finalRowLabel.includes(String(market.line)) && (
                              <span className="font-monument text-[9px] text-gray-400 flex-shrink-0 px-1.5 py-0.5 bg-gray-100 dark:bg-[#1e1e24] rounded-[5px]">
                                {market.line > 0 ? `+${market.line}` : market.line}
                              </span>
                            )}
                          </div>
                          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                            {formatVolume(market.volume)} {t("chartMeta.volSuffix")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 self-center w-full md:w-auto justify-end order-3 md:order-none">
                          {market.outcomes.slice(0, 2).map((outcome: string, oIdx: number) => {
                            const isActive = isSelected && selectedOutcomeIdx === oIdx;
                            const isGreen = oIdx === 0;
                            const tokenId = market.clobTokenIds[oIdx];
                            const p = (tokenId ? allLivePrices[tokenId] : undefined) ?? market.outcomePrices[oIdx] ?? 0;
                            // Shrink long team names to alias/abbreviation
                            // inside the compact button — "RCD Espanyol de
                            // Barcelona" won't fit at 100px. For tennis the
                            // outcome is a player full name ("Yasmine
                            // Kabbaj") with no team alias, so we collapse
                            // to the uppercase surname ("KABBAJ") to match
                            // how Polymarket labels these buttons.
                            const isTennisLeague = game?.league === 'atp' || game?.league === 'wta';
                            const shortOutcome = (() => {
                              const oLower = outcome.toLowerCase();
                              // Localised outcome labels for non-team
                              // markets (Draw on 3-way moneyline, generic
                              // Yes/No fallback). Polymarket already ships
                              // outcomes localised under ?locale=es for
                              // most markets, but the negRisk Draw row's
                              // outcome can still come back literal "Draw".
                              if (oLower === 'draw') return t("sportsMatch.draw");
                              // Compact "Over <line>" / "Under <line>" → "O
                              // <line>" / "U <line>" so the button stays
                              // narrow. Two cases: outcome is bare "Over" /
                              // "Under" with line on the market, or outcome
                              // already includes the line in its text.
                              if ((oLower === 'over' || oLower === 'under') && typeof market.line === 'number') {
                                return `${oLower === 'over' ? 'O' : 'U'} ${market.line}`;
                              }
                              const ouMatch = outcome.trim().match(/^(over|under)\s+(.+)$/i);
                              if (ouMatch) {
                                return `${ouMatch[1].toLowerCase() === 'over' ? 'O' : 'U'} ${ouMatch[2]}`;
                              }
                              if (isTennisLeague) {
                                // Prefer the team's `abbreviation` field
                                // (e.g. "coppeja") over a derived surname
                                // ("COPPEJANS") — gamma's abbreviation is
                                // what Polymarket itself ships on the
                                // trade pills, so this keeps us aligned.
                                for (const t of [teams?.home, teams?.away]) {
                                  if (!t?.name) continue;
                                  if (oLower.includes(t.name.toLowerCase()) && t.abbreviation) {
                                    return String(t.abbreviation).toUpperCase();
                                  }
                                }
                                const parts = outcome.trim().split(/\s+/);
                                const surname = parts.length > 1 ? parts[parts.length - 1] : outcome;
                                return surname.toUpperCase();
                              }
                              // Only replace when the outcome itself CONTAINS
                              // the full team name ("Rayo Vallecano de Madrid"
                              // matches, "No" / "Yes" don't). The reverse
                              // match would break BTTS rows where "No" is a
                              // substring of "Vallecano".
                              const teamForOutcome = (() => {
                                for (const t of [teams?.home, teams?.away]) {
                                  if (!t) continue;
                                  const nameLower = (t.name || '').toLowerCase();
                                  if (nameLower && nameLower.length >= 4 && oLower.includes(nameLower)) return t;
                                }
                                return null;
                              })();
                              if (!teamForOutcome) return outcome;
                              // PM uses the team's `abbreviation` field
                              // ("STL" / "PIT" for MLB) on the trade
                              // pills, not the alias ("Cardinals" /
                              // "Pirates"). Mirror that — fall back to
                              // alias / outcome only when no abbr.
                              return (teamForOutcome.abbreviation?.toUpperCase() || teamForOutcome.alias || outcome).toUpperCase();
                            })();
                            // Match /event EventMarketsGrid desktop style:
                            // fixed-width pill, two spans (subtle abbr +
                            // bold price), green/red tints, full color when
                            // selected. Third outcome (3-way moneyline) gets
                            // a neutral gray pill.
                            const isYesSide = oIdx === 0;
                            const isNoSide = oIdx === 1;
                            const priceCents = formatPrice(p).replace('¢', '');
                            const bgClass = isYesSide
                              ? isActive ? "bg-[#359a5e] hover:bg-[#2d8450]" : "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                              : isNoSide
                              ? isActive ? "bg-[#cb3131] hover:bg-[#b82a2a]" : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25"
                              : isActive ? "bg-gray-700 dark:bg-[#2a2a32]" : "bg-gray-100 dark:bg-[#1a1a22] hover:bg-gray-200 dark:hover:bg-[#22222a]";
                            const labelClass = isYesSide
                              ? isActive ? "text-white/80" : "text-[#3db468]/80"
                              : isNoSide
                              ? isActive ? "text-white/80" : "text-[#cb3131]/80"
                              : isActive ? "text-white/80" : "text-gray-500 dark:text-gray-400";
                            const priceClass = isYesSide
                              ? isActive ? "text-white" : "text-[#3db468]"
                              : isNoSide
                              ? isActive ? "text-white" : "text-[#cb3131]"
                              : isActive ? "text-white" : "text-gray-700 dark:text-gray-200";
                            return (
                              <button
                                key={oIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveMarketType(activeTab);
                                  setSelectedMarketIdx(mIdx);
                                  setSelectedOutcomeIdx(oIdx);
                                  setTradingFlash(true);
                                  setTimeout(() => setTradingFlash(false), 600);
                                  // Mobile: open the trading modal directly
                                  // (no bottom-sheet overlay). Desktop keeps
                                  // the right-column TradingPanel updating
                                  // in place via the selection state above.
                                  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                                    const liveYes = market.clobTokenIds?.[0] ? allLivePrices[market.clobTokenIds[0]] : undefined;
                                    const liveNo = market.clobTokenIds?.[1] ? allLivePrices[market.clobTokenIds[1]] : undefined;
                                    const yesPrice = (liveYes ?? Number(market.outcomePrices?.[0])) || 0.5;
                                    const noPrice = (liveNo ?? Number(market.outcomePrices?.[1])) || 0.5;
                                    const tappedPrice = oIdx === 0 ? yesPrice : noPrice;
                                    const altPrice = oIdx === 0 ? noPrice : yesPrice;
                                    const isTennis = game?.league === 'atp' || game?.league === 'wta';
                                    // Same precedence as the row pills:
                                    // prefer gamma's abbreviation, fall
                                    // back to derived surname.
                                    const tennisLabelFor = (full: string) => {
                                      const fLower = (full || '').toLowerCase();
                                      for (const t of [teams?.home, teams?.away]) {
                                        if (!t?.name) continue;
                                        if (fLower.includes(t.name.toLowerCase()) && t.abbreviation) {
                                          return String(t.abbreviation).toUpperCase();
                                        }
                                      }
                                      const parts = (full || '').trim().split(/\s+/);
                                      return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : (full || '').toUpperCase();
                                    };
                                    const modalOutcomes = isTennis
                                      ? (market.outcomes || []).map((o: string) => tennisLabelFor(o))
                                      : market.outcomes;
                                    openPolymarket({
                                      marketSlug: market.slug,
                                      outcome: oIdx === 0 ? 'YES' : 'NO',
                                      outcomePrice: tappedPrice,
                                      altOutcomePrice: altPrice,
                                      // Empty question + no image — the
                                      // outcome pills carry the context.
                                      // Hero already shows the event image
                                      // and player names directly above.
                                      marketQuestion: '',
                                      marketImage: undefined,
                                      actualOutcome: modalOutcomes?.[oIdx],
                                      outcomes: modalOutcomes,
                                      clobTokenIds: market.clobTokenIds || [],
                                      conditionId: market.conditionId,
                                      onPriceAlert: () => setAlertModalOpen(true),
                                      disableSwipeDismiss: true,
                                    } as any);
                                  }
                                }}
                                className={`inline-flex items-center justify-center gap-1.5 flex-1 md:flex-none px-4 py-2.5 rounded-[5px] transition-colors whitespace-nowrap ${bgClass}`}
                              >
                                <span className={`text-xs font-inter-tight font-bold uppercase ${labelClass}`}>{shortOutcome}</span>
                                {/* Fixed width slot keeps the button from
                                    resizing as the price ticks between
                                    "9¢", "50¢" and "100¢" — without it
                                    the row jitters on every WS update. */}
                                <span className={`text-sm font-inter-tight font-bold tabular-nums inline-block min-w-[3.1rem] text-right ${priceClass}`}>{priceCents}¢</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          );
        })()}
      </div>

          {/* Mobile only: Trading — hidden to mirror /event's mobile flow
               (selected outcome + PlaceTrade live in a bottom sheet, not
               inline below the hero). Block kept in the tree so we can
               turn it back on cheaply if we add a sheet trigger here. */}
          {currentMarket && (
            <div className="hidden space-y-3">
              <MarketPriceProvider
                clobTokenIds={currentMarket.clobTokenIds || []}
                initialPrices={currentMarket.outcomePrices || []}
                realtimeUpdates
              >
                <RealTradingPanel
                  marketData={{
                    slug: currentMarket.slug,
                    question: currentMarket.groupItemTitle || currentMarket.question,
                    outcomePrices: currentMarket.outcomePrices || [],
                    // Polymarket returns moneyline outcomes as full legal team
                    // names ("Club Atlético de Madrid"). Replace with the
                    // 3-letter abbreviation so the trade buttons read
                    // "ATM 12¢ / ARS 89¢" — same convention PM uses on
                    // their sports event pages. Yes/No/Draw outcomes pass
                    // through untouched.
                    outcomes: abbreviateOutcomesForSports(
                      currentMarket.outcomes || [],
                      teams?.home,
                      teams?.away,
                    ),
                    clobTokenIds: currentMarket.clobTokenIds || [],
                    image: null,
                  }}
                  isActive={currentMarket.active !== false}
                  conditionId={currentMarket.conditionId}
                  eventSlug={slug}
                  eventTitle={event?.title}
                  initialOutcomeIndex={selectedOutcomeIdx === 1 ? 1 : 0}
                  onOutcomeChange={setSelectedOutcomeIdx}
                />
              </MarketPriceProvider>
            </div>
          )}

          {/* Mobile-only My Positions — mirrors /event's pattern of showing
               just the positions list under the markets grid (no tabs, no
               OrderBook, no Trades — those live in the desktop sidebar).
               Sits right after the markets so phone users see their
               exposure without scrolling past Market Talks. */}
          {currentMarket && (
            <div className="lg:hidden bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
              <MyPositions
                conditionId={currentMarket.conditionId}
                acceptingOrders={currentMarket.active !== false}
                marketQuestion={currentMarket.groupItemTitle || currentMarket.question}
                outcomes={currentMarket.outcomes}
                source="polymarket"
                allowedConditionIds={allEventConditionIds}
                allowedMarketSlugs={allEventMarketSlugs}
                marketMetaByConditionId={marketMetaByConditionId}
              />
            </div>
          )}

          {currentMarket && (
            <div className="mt-3 space-y-3">
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]">
                {/* Pass BOTH eventId and seriesId: match-level events on
                    PM often carry 0 comments (people discuss at the
                    series/tournament level instead — e.g. Madrid Open
                    series 10365 has the actual chatter). Backend merges
                    both feeds. */}
                <MarketTalks
                  marketSlug={event?.slug || currentMarket.slug}
                  marketQuestion={event?.title || currentMarket.groupItemTitle || currentMarket.question}
                  marketSource="polymarket"
                  eventId={event?.id}
                  seriesId={event?.series?.[0]?.id}
                />
              </div>

              {/* Resolution Details — same component the regular
                  /event page uses. `selectedMarketDetails` is the rich
                  payload from `useMarketPageQuery` (description,
                  resolutionSource, createdAt, resolvedBy, settlement
                  timer). `fullEventData` covers the event-level
                  description fallback. */}
              {fullEventData && currentMarket && selectedMarketDetails && (
                <ResolutionDetails
                  source="polymarket"
                  eventData={fullEventData}
                  selectedMarketDetails={selectedMarketDetails}
                  selectedMarket={currentMarket as any}
                />
              )}

              {/* FAQ — generated server-side from event data. Same
                  layout/copy pattern as the regular event page. */}
              {faqs.length > 0 && (
                <div
                  className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 md:p-6"
                  onMouseEnter={() => setFaqHovered(true)}
                  onMouseLeave={() => setFaqHovered(false)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpenTextIcon size={20} className="text-[#7C3AED] dark:text-[#CCFF00]" isHovered={faqHovered} />
                    <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">{t("section.faq")}</h3>
                  </div>
                  <p className="font-inter-tight text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {t("section.faqSubtitle")}
                  </p>
                  <div className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                    {faqs.map((faq, i) => (
                      <details key={i} className="group">
                        <summary className="flex items-start gap-3 cursor-pointer list-none py-3.5 px-1 -mx-1 rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12]/60 transition-colors">
                          <svg
                            className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 shrink-0 transition-transform duration-200 group-open:rotate-90 group-open:text-[#7C3AED] dark:group-open:text-[#CCFF00]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.25}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                            {faq.question}
                          </span>
                        </summary>
                        <div className="pl-7 pr-1 pb-4 pt-1">
                          <p className="font-satoshi text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* MoreGames moved to the sidebar — keeps the main column
                  focused on the match itself + markets + comments. */}
            </div>
          )}

        </div>

        {/* ── RIGHT COLUMN — Trading + OrderBook + MoreGames ── */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="sticky top-4 space-y-3">
            {/* Selected market header */}
            {currentMarket && (() => {
              const rawTitle = currentMarket.groupItemTitle || currentMarket.question;
              // Strip league prefix: "Indian Premier League: Team A vs Team B" → "Team A vs Team B"
              const stripped = rawTitle.includes(': ') ? rawTitle.split(': ').slice(1).join(': ') : rawTitle;
              // Binary moneyline / Map Winner (esports/tennis): show selected outcome name (team)
              const moneylineLikeTypes = new Set(['moneyline', 'child_moneyline', 'first_half_moneyline']);
              const isBinaryMoneyline = moneylineLikeTypes.has(currentMarket.sportsMarketType) && currentMarket.outcomes?.length === 2 && !currentMarket.outcomes.some((o: string) => ['yes', 'no'].includes(o.toLowerCase()));
              const teamName = currentMarket.outcomes?.[selectedOutcomeIdx] || '';
              // Map N Winner: prefix with "Map N - "
              const mapMatch = currentMarket.slug?.match(/-game(\d+)/);
              const baseTitle = isBinaryMoneyline
                ? (mapMatch ? `Map ${mapMatch[1]} - ${teamName}` : teamName)
                : stripped;
              const aliased = shortenTitleWithAliases(baseTitle, teams?.home, teams?.away);
              const isDraw = /^\s*Draw\b/i.test(aliased);
              // "Draw (Team A vs Team B)" → "Draw" — redundant with the hero.
              const title = isDraw ? t("sportsMatch.draw") : aliased;
              const teamLogo = isDraw ? null
                : teams?.home?.name && baseTitle.includes(teams.home.name) ? teams.home.logo
                : teams?.away?.name && baseTitle.includes(teams.away.name) ? teams.away.logo
                : null;
              const img = teamLogo || event?.image;
              return (
                <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {img && (
                        <CdnImage src={img} alt="" width={28} height={28} className="w-7 h-7 rounded-[5px] object-contain flex-shrink-0" />
                      )}
                      <span className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white truncate">
                        {title}
                      </span>
                    </div>
                    <button
                      onClick={() => setAlertModalOpen(true)}
                      className="flex-shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[5px] bg-amber-500 hover:bg-amber-600 text-white transition-colors group"
                      title={t("sportsEvent.setPriceAlert")}
                    >
                      <FiBell className="w-3.5 h-3.5 group-hover:animate-bell-ring" strokeWidth={2.4} />
                      <span className="text-[11px] font-inter-tight font-semibold">{t("sportsEvent.alert")}</span>
                    </button>
                  </div>
                </div>
              );
            })()}
            {currentMarket && (
              // realtimeUpdates={false} on purpose — the page already
              // subscribes to CLOB WS directly via `subscribeToToken`
              // (driving the moneyline buttons) and we feed those values
              // into `initialPrices` here. The provider's own backend
              // Socket.IO relay would otherwise override our fresher
              // direct-CLOB prices with a stale value, so the sidebar
              // and the buttons drifted apart.
              <MarketPriceProvider
                clobTokenIds={currentMarket.clobTokenIds || []}
                initialPrices={currentMarketLivePrices.length > 0 ? currentMarketLivePrices : (currentMarket.outcomePrices || [])}
                realtimeUpdates={false}
              >
                <RealTradingPanel
                  marketData={{
                    slug: currentMarket.slug,
                    question: currentMarket.groupItemTitle || currentMarket.question,
                    outcomePrices: currentMarketLivePrices.length > 0 ? currentMarketLivePrices : (currentMarket.outcomePrices || []),
                    // Polymarket returns moneyline outcomes as full legal team
                    // names ("Club Atlético de Madrid"). Replace with the
                    // 3-letter abbreviation so the trade buttons read
                    // "ATM 12¢ / ARS 89¢" — same convention PM uses on
                    // their sports event pages. Yes/No/Draw outcomes pass
                    // through untouched.
                    outcomes: abbreviateOutcomesForSports(
                      currentMarket.outcomes || [],
                      teams?.home,
                      teams?.away,
                    ),
                    clobTokenIds: currentMarket.clobTokenIds || [],
                    image: null,
                  }}
                  isActive={currentMarket.active !== false}
                  conditionId={currentMarket.conditionId}
                  eventSlug={slug}
                  eventTitle={event?.title}
                  initialOutcomeIndex={selectedOutcomeIdx === 1 ? 1 : 0}
                  onOutcomeChange={setSelectedOutcomeIdx}
                />
              </MarketPriceProvider>
            )}

            {currentMarket && (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                <MyPositions
                  conditionId={currentMarket.conditionId}
                  acceptingOrders={currentMarket.active !== false}
                  marketQuestion={currentMarket.groupItemTitle || currentMarket.question}
                  outcomes={currentMarket.outcomes}
                  source="polymarket"
                  allowedConditionIds={allEventConditionIds}
                  allowedMarketSlugs={allEventMarketSlugs}
                  marketMetaByConditionId={marketMetaByConditionId}
                />
              </div>
            )}

            {currentMarket && (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                <OpenOrders marketSlug={currentMarket.slug} conditionId={currentMarket.conditionId} outcomes={currentMarket.outcomes} />
              </div>
            )}

            {currentMarket && (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                <SourceAwareOrderBook
                  source="polymarket"
                  tokenIds={currentMarket.clobTokenIds}
                  outcomes={currentMarket.outcomes}
                  acceptingOrders={currentMarket.active !== false}
                  initialOutcomeIndex={selectedOutcomeIdx}
                  orderbookApi="sports-live"
                />
              </div>
            )}

            {currentMarket && isEsportsRoute && (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                <SourceAwareTrades
                  source="polymarket"
                  marketSlug={currentMarket.slug}
                  clobTokenIds={currentMarket.clobTokenIds}
                  conditionId={currentMarket.conditionId}
                  marketQuestion={currentMarket.groupItemTitle || currentMarket.question}
                  outcomes={currentMarket.outcomes}
                  isActive={currentMarket.active !== false}
                />
              </div>
            )}

            {currentMarket && (
              <MoreGames currentSlug={slug} league={game.league} isEsports={isEsportsRoute} />
            )}

          </div>
        </div>
      </div>

      {/* Livestream PiP Widget */}
      {activeLivestream && (
        <LivestreamPiP stream={activeLivestream} onClose={() => setActiveLivestream(null)} />
      )}

      {currentMarket && alertModalOpen && (
        <PriceAlertModal
          isOpen={alertModalOpen}
          onClose={() => setAlertModalOpen(false)}
          marketTitle={currentMarket.groupItemTitle || currentMarket.question}
          marketSlug={currentMarket.slug}
          eventSlug={slug}
          marketId={currentMarket.conditionId || currentMarket.slug}
          tokenIds={currentMarket.clobTokenIds || []}
          outcomes={currentMarket.outcomes || ["Yes", "No"]}
          currentPrices={currentMarket.outcomePrices || [0.5, 0.5]}
          source="polymarket"
          marketImage={event?.image || null}
        />
      )}

      {/* Mobile bottom sheet — opens when the user taps a moneyline
          outcome on phone. Same popover pattern /event uses; hosts the
          price-alert bell + a Buy CTA that launches the trading modal. */}
      {currentMarket && mobileTradeOpen && (() => {
        const isTennis = game?.league === 'atp' || game?.league === 'wta';
        const surnameOf = (full: string) => {
          const fLower = (full || '').toLowerCase();
          for (const t of [teams?.home, teams?.away]) {
            if (!t?.name) continue;
            if (fLower.includes(t.name.toLowerCase()) && t.abbreviation) {
              return String(t.abbreviation).toUpperCase();
            }
          }
          const parts = (full || '').trim().split(/\s+/);
          return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : (full || '').toUpperCase();
        };
        const sheetOutcomes = isTennis
          ? (currentMarket.outcomes || []).map((o: string) => surnameOf(o))
          : currentMarket.outcomes;
        const rawQ = currentMarket.groupItemTitle || currentMarket.question;
        const sheetQuestion = isTennis
          ? rawQ.replace(/([A-Z][a-z]+(?:-[A-Z][a-z]+)?)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g, (_m: string, first: string, last: string) => `${first.split('-').map((p: string) => p[0].toUpperCase()).join('-')}. ${last}`)
          : rawQ;
        const sheetMarket: EventMarket = {
          id: currentMarket.id,
          question: sheetQuestion,
          groupItemTitle: sheetQuestion,
          slug: currentMarket.slug,
          conditionId: currentMarket.conditionId,
          outcomes: sheetOutcomes,
          outcomePrices: currentMarket.outcomePrices,
          clobTokenIds: currentMarket.clobTokenIds,
          acceptingOrders: currentMarket.active !== false,
          active: currentMarket.active !== false,
          closed: false,
          volume: Number(currentMarket.volume) || 0,
          liquidity: 0,
          enableOrderBook: true,
          image: event?.image || null,
          imageUrl: event?.image || null,
        };
        const livePriceFor = (m: { clobTokenIds?: string[]; outcomePrices?: number[] }): [number, number] => {
          const t0 = m.clobTokenIds?.[0];
          const t1 = m.clobTokenIds?.[1];
          const p0 = (t0 ? allLivePrices[t0] : undefined) ?? Number(m.outcomePrices?.[0]) ?? 0.5;
          const p1 = (t1 ? allLivePrices[t1] : undefined) ?? Number(m.outcomePrices?.[1]) ?? 0.5;
          return [p0, p1];
        };
        return (
          <MobileBottomSheet
            selectedMarket={sheetMarket}
            activeMarketsCount={1}
            source="polymarket"
            mobileTradeOpen={mobileTradeOpen}
            setMobileTradeOpen={setMobileTradeOpen}
            setAlertModalOpen={setAlertModalOpen}
            getLivePrice={livePriceFor as any}
            getLiveBuyPrices={((m: any) => {
              const [yes, no] = livePriceFor(m);
              return { buyYes: yes, buyNo: no, isRealOrderbook: false };
            }) as any}
            openTradingModal={(params) => {
              if (!params || !params.isOpen) return;
              openPolymarket({
                marketSlug: params.marketSlug,
                outcome: params.outcome,
                outcomePrice: params.outcomePrice,
                altOutcomePrice: 1 - params.outcomePrice,
                marketQuestion: params.marketQuestion,
                marketImage: event?.image || undefined,
                actualOutcome: params.actualOutcome,
                outcomes: params.outcomes,
                clobTokenIds: currentMarket.clobTokenIds || [],
                conditionId: currentMarket.conditionId,
              } as any);
            }}
            initialOutcomeIndex={selectedOutcomeIdx === 1 ? 1 : 0}
          />
        );
      })()}

    </div>
  );
}

